import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import KnowledgeBaseTab from './KnowledgeBaseTab';
import { SendMessagePanel } from './AdminPanel_SendMessage';

import './AdminPanel.css';

// ─── Language & Client lookup tables ─────────────────────────────────────────
const LANG_MAP = {
  1:  { code: 'de', label: 'Deutsch' },
  2:  { code: 'en', label: 'English' },
  3:  { code: 'ru', label: 'Русский' },
  4:  { code: 'ar', label: 'عربي' },
  5:  { code: 'fr', label: 'Français' },
  6:  { code: 'es', label: 'Español' },
  7:  { code: 'pl', label: 'Polski' },
  8:  { code: 'ro', label: 'Română' },
  9:  { code: 'tr', label: 'Türkçe' },
  10: { code: 'hr', label: 'Hrvatski' },
};

/** Get localised name from an item, preferring currentLanguage, always also showing EN */
const getLocalName = (item, currentLanguage) => {
  const code = currentLanguage || 'de';
  const col = code === 'de' ? 'name' : `name_${code}`;
  return item[col] || item.name_en || item.name || `ID ${item.id}`;
};

const getLocalDesc = (item, currentLanguage) => {
  const code = currentLanguage || 'de';
  const col = code === 'de' ? 'description' : `description_${code}`;
  return item[col] || item.description_en || item.description || '';
};

const truncate = (str, max = 300) =>
  str && str.length > max ? str.slice(0, max).trimEnd() + '…' : str;

// ─── tiny api helper ─────────────────────────────────────────────────────────
const api = async (endpoint, options = {}) => {
  const { appConfig } = await import('../config');
  const API_BASE = appConfig.apiBaseUrl;

  const doFetch = (token) => {
    const headers = { 'Authorization': `Bearer ${token}`, ...options.headers };
    if (!(options.body instanceof FormData) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    return fetch(`${API_BASE}${endpoint}`, { ...options, headers });
  };

  let accessToken = localStorage.getItem('hejvi_access_token');
  if (!accessToken) {
    ['hejvi_access_token','hejvi_refresh_token','hejvi_token_expiry','hejvi_user'].forEach(k => localStorage.removeItem(k));
    window.location.href = '/login';
    throw new Error('No access token available');
  }

  let response = await doFetch(accessToken);

  // Bei 401: einmal Token-Refresh versuchen
  if (response.status === 401) {
    const refreshTok = localStorage.getItem('hejvi_refresh_token');
    if (refreshTok) {
      try {
        const refreshRes = await fetch(`${API_BASE}/refresh`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${refreshTok}`, 'Content-Type': 'application/json' },
        });
        if (refreshRes.ok) {
          const data = await refreshRes.json();
          localStorage.setItem('hejvi_access_token', data.access_token);
          if (data.refresh_token) {
            localStorage.setItem('hejvi_refresh_token', data.refresh_token);
          }
          localStorage.setItem('hejvi_token_expiry', String(Date.now() + 15 * 60 * 1000));
          // Features aus Refresh-Response an AuthContext weitergeben
          if (data.features) {
            window.dispatchEvent(new CustomEvent('hejvi:features-updated', { detail: data.features }));
          }
          response = await doFetch(data.access_token);
        } else {
          ['hejvi_access_token','hejvi_refresh_token','hejvi_token_expiry','hejvi_user'].forEach(k => localStorage.removeItem(k));
          window.location.href = '/login';
          throw new Error('Authentication failed');
        }
      } catch (e) {
        if (e.message === 'Authentication failed') throw e;
        throw new Error(`HTTP 401: ${endpoint}`);
      }
    } else {
      ['hejvi_access_token','hejvi_refresh_token','hejvi_token_expiry','hejvi_user'].forEach(k => localStorage.removeItem(k));
      window.location.href = '/login';
      throw new Error('Authentication failed');
    }
  }

  if (!response.ok) {
    // 402 = Lizenz abgelaufen — Custom Event feuern damit App-Root
    // ein Overlay zeigen kann (AuthContext hat keinen Zugriff auf api())
    if (response.status === 402) {
      const data = await response.json().catch(() => ({}));
      const msg = data.error || 'Lizenz abgelaufen. Bitte Lizenz erneuern.';
      window.dispatchEvent(new CustomEvent('hejvi:licence-expired', { detail: { message: msg } }));
      const err = new Error(msg);
      err.code = 'LICENCE_EXPIRED';
      throw err;
    }
    // 403 = Feature-Gate (Tier/Seat-Typ)
    if (response.status === 403) {
      const data = await response.json().catch(() => ({}));
      const msg = data.error || 'Zugriff verweigert. Lizenz-Upgrade erforderlich.';
      const err = new Error(msg);
      err.code = 'FEATURE_GATED';
      throw err;
    }
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  return response.json();
};

// ─── Sub-components ──────────────────────────────────────────────────────────

/** Pill badge */
const StatusBadge = ({ status, onToggle }) => {
  const { t } = useLanguage();
  const label = status === 1 ? t('adm_active') : t('adm_inactive');
  if (onToggle) {
    return (
      <button
        className={`adm-badge adm-badge--toggle ${status === 1 ? 'adm-badge--active' : 'adm-badge--inactive'}`}
        onClick={onToggle}
        title={status === 1 ? t('adm_deactivate') : t('adm_activate')}
        type="button"
      >
        {label}
      </button>
    );
  }
  return (
    <span className={`adm-badge ${status === 1 ? 'adm-badge--active' : 'adm-badge--inactive'}`}>
      {label}
    </span>
  );
};

/** Inline confirm button — disabled variant protects last superadmin */
const DangerButton = ({ label, onConfirm, disabled, disabledTitle }) => {
  const { t } = useLanguage();
  const [asking, setAsking] = useState(false);
  if (disabled) {
    return (
      <button
        className="adm-btn adm-btn--ghost adm-btn--danger-ghost adm-btn--disabled"
        title={disabledTitle}
        disabled
        style={{ opacity: 0.35, cursor: 'not-allowed' }}
      >
        {label}
      </button>
    );
  }
  if (!asking)
    return (
      <button className="adm-btn adm-btn--ghost adm-btn--danger-ghost" onClick={() => setAsking(true)}>
        {label}
      </button>
    );
  return (
    <span className="adm-confirm-row">
      <span className="adm-confirm-label">{t ? t('adm_confirm') : 'Sicher?'}</span>
      <button className="adm-btn adm-btn--danger adm-btn--xs" onClick={() => { setAsking(false); onConfirm(); }}>{t ? t('adm_yes') : 'Ja'}</button>
      <button className="adm-btn adm-btn--ghost adm-btn--xs" onClick={() => setAsking(false)}>{t ? t('adm_no') : 'Nein'}</button>
    </span>
  );
};

/** Toast notification */
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [message, onClose]);
  return (
    <div className={`adm-toast adm-toast--${type}`} onClick={onClose}>
      <span>{message}</span>
    </div>
  );
};

// ─── QR-Code Anzeige ────────────────────────────────────────────────────────
/**
 * Generiert einen QR-Code aus einem URL-String.
 * Lädt das 'qrcode'-Paket lazy — falls nicht installiert: npm install qrcode
 */
const QRCodeDisplay = ({ value, size = 148, label }) => {
  const canvasRef = React.useRef(null);
  const [ready, setReady] = React.useState(false);
  const [err, setErr]     = React.useState(false);

  React.useEffect(() => {
    if (!value) return;
    setReady(false); setErr(false);
    import('qrcode').then(mod => {
      const QRCode = mod.default || mod;
      QRCode.toCanvas(canvasRef.current, value, {
        width: size, margin: 1,
        color: { dark: '#111827', light: '#ffffff' },
      }).then(() => setReady(true)).catch(() => setErr(true));
    }).catch(() => setErr(true));
  }, [value, size]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div style={{
        borderRadius: 10, border: '1px solid #E5E7EB', overflow: 'hidden',
        background: '#fff', display: 'inline-flex',
        opacity: ready ? 1 : 0.3, transition: 'opacity .3s',
      }}>
        <canvas ref={canvasRef} width={size} height={size} />
      </div>
      {err && <span style={{ fontSize: 11, color: '#9CA3AF' }}>QR nicht verfügbar — npm install qrcode</span>}
      {label && <span style={{ fontSize: 11, color: '#6B7280', fontFamily: 'monospace', wordBreak: 'break-all', maxWidth: size, textAlign: 'center' }}>{label}</span>}
    </div>
  );
};

// ─── PIN-Erstell-Modal ───────────────────────────────────────────────────────
/**
 * Zeigt PIN und QR-Code nach Erstellung oder Reset — einmalig.
 * Der Klartext-PIN wird vom Backend nur bei diesem einen Request zurückgegeben.
 */
const PinCreatedModal = ({ pin, qrToken, userName, onClose }) => {
  const [copied, setCopied] = React.useState(false);
  const loginUrl = qrToken ? `${window.location.origin}/login?t=${qrToken}` : null;

  const copyPin = () => {
    navigator.clipboard.writeText(pin).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2500); });
  };

  return (
    <Modal title={`🔐 Login-Daten: ${userName}`} onClose={onClose}>
      <div style={{ textAlign: 'center', padding: '4px 0 12px' }}>
        <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 20 }}>
          Diese Daten jetzt sichern — der PIN wird <strong>nicht</strong> erneut angezeigt.
        </p>

        {/* PIN-Box */}
        <div style={{
          background: '#fefce8', border: '2px solid #f59e0b', borderRadius: 12,
          padding: '18px 20px', marginBottom: 18,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
            PIN (8 Zeichen)
          </div>
          <div style={{ fontFamily: 'monospace', fontSize: 30, fontWeight: 800, letterSpacing: '.18em', color: '#1c1917', marginBottom: 12 }}>
            {pin}
          </div>
          <button
            onClick={copyPin}
            style={{ background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 7,
              padding: '7px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
          >
            {copied ? '✅ Kopiert!' : '📋 PIN kopieren'}
          </button>
        </div>

        {/* QR-Code */}
        {loginUrl && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 10 }}>
              QR-Code zum Einloggen
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <QRCodeDisplay value={loginUrl} size={160} />
            </div>
            <p style={{ fontSize: 11, color: '#9CA3AF', marginTop: 8 }}>
              Scannen → direkt einloggen ohne PIN-Eingabe
            </p>
          </div>
        )}

        <p style={{ fontSize: 11, color: '#9CA3AF' }}>
          Der QR-Code ist dauerhaft im Nutzer-Eintrag abrufbar.<br/>
          Den PIN jetzt ausdrucken oder sicher übermitteln.
        </p>
      </div>
      <div className="adm-modal__footer">
        <button className="adm-btn adm-btn--primary" onClick={onClose}>✓ Verstanden &amp; schließen</button>
      </div>
    </Modal>
  );
};

/** Generic search/filter bar */
const SearchBar = ({ value, onChange, placeholder }) => (
  <div className="adm-search">
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
      <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="2"/>
      <path d="M14.5 14.5L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder || 'Suchen…'}
      className="adm-search__input"
    />
    {value && (
      <button className="adm-search__clear" onClick={() => onChange('')}>×</button>
    )}
  </div>
);

/** Status filter segmented control: Alle / Aktiv / Inaktiv */
const StatusFilter = ({ value, onChange }) => {
  const { t } = useLanguage();
  const options = [
    { val: 'all',      label: t('adm_filter_all')      || 'Alle' },
    { val: 'active',   label: t('adm_filter_active')   || 'Aktiv' },
    { val: 'inactive', label: t('adm_filter_inactive') || 'Inaktiv' },
  ];
  return (
    <div className="adm-status-filter">
      {options.map(o => (
        <button
          key={o.val}
          className={`adm-status-filter__btn ${value === o.val ? 'adm-status-filter__btn--active' : ''}`}
          onClick={() => onChange(o.val)}
          type="button"
        >
          {o.label}
        </button>
      ))}
    </div>
  );
};

/** Modal wrapper */
const Modal = ({ title, onClose, children, wide }) => (
  <div className="adm-modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
    <div className={`adm-modal ${wide ? 'adm-modal--wide' : ''}`}>
      <div className="adm-modal__header">
        <h3 className="adm-modal__title">{title}</h3>
        <button className="adm-modal__close" onClick={onClose}>×</button>
      </div>
      <div className="adm-modal__body">{children}</div>
    </div>
  </div>
);

const Field = ({ label, children, hint }) => (
  <div className="adm-field">
    <label className="adm-field__label">{label}</label>
    {children}
    {hint && <p className="adm-field__hint">{hint}</p>}
  </div>
);

// ─── Stats Dashboard ─────────────────────────────────────────────────────────
const StatsBar = ({ stats, onTabClick, validTabIds = [] }) => {
  const { t } = useLanguage();
  const allCards = [
    { label: t('adm_stat_users'),          value: stats?.users ?? '–',        icon: '👥', tab: 'users' },
    { label: t('adm_stat_active'),         value: stats?.active_users ?? '–', icon: '✅', tab: 'users' },
    { label: t('adm_groups'),              value: stats?.usergroups ?? '–',   icon: '🏷️', tab: 'usergroups' },
    { label: t('adm_videos'),              value: stats?.elements ?? '–',     icon: '🎬', tab: 'elements' },
    { label: t('adm_topics'),              value: stats?.collections ?? '–',  icon: '📚', tab: 'collections' },
    { label: t('adm_docs') || 'Dokumente', value: stats?.docs_total ?? '–',   icon: '📄', tab: 'documents' },
  ];
  // Nur Karten zeigen deren Tab auch wirklich verfügbar ist (gleiche Logik wie Nav-Tabs)
  const cards = allCards.filter(c => validTabIds.includes(c.tab));
  return (
    <div className="adm-stats">
      {cards.map(c => (
        <div
          key={c.label}
          className="adm-stat-card"
          onClick={() => onTabClick && onTabClick(c.tab)}
          title={`Zu ${c.label} springen`}
        >
          <span className="adm-stat-card__icon">{c.icon}</span>
          <span className="adm-stat-card__value">{c.value}</span>
          <span className="adm-stat-card__label">{c.label}</span>
        </div>
      ))}
    </div>
  );
};

// ─── QUICK MESSAGE MODAL ──────────────────────────────────────────────────────
const QuickMessageModal = ({ targetType, targetId, targetName, onClose, toast }) => {
  const { t, currentLanguage } = useLanguage();
  const validLangs  = LANG_OPTIONS_NOTIF.map(l => l.code);
  const defaultLang = validLangs.includes(currentLanguage) ? currentLanguage : 'de';

  const [title,   setTitle]   = React.useState('');
  const [body,    setBody]     = React.useState('');
  const [sending, setSending] = React.useState(false);

  const canSubmit = title.trim().length > 0 && body.trim().length > 0 && !sending;

  const handleSend = async () => {
    if (!canSubmit) return;
    setSending(true);
    try {
      const res = await api('/admin/notifications/send', {
        method: 'POST',
        body: JSON.stringify({
          title:       title.trim(),
          body:        body.trim(),
          source_lang: defaultLang,
          target_type: targetType,
          target_id:   targetId,
        }),
      });
      if (res?.status === 'ok') {
        toast(`✓ ${t('adm_notifications') || 'Nachricht'} ${t('adm_toast_saved') || 'gesendet'}`, 'success');
        onClose();
      } else {
        throw new Error(res?.message || 'Fehler');
      }
    } catch (e) {
      toast(`⚠ ${e.message || t('adm_toast_error')}`, 'error');
    } finally {
      setSending(false);
    }
  };

  const icon = targetType === 'user' ? '👤' : '👥';

  return (
    <Modal
      title={`📢 ${t('adm_notifications') || 'Nachricht'} → ${icon} ${targetName}`}
      onClose={onClose}
    >
      <p style={{ fontSize: 12, color: '#9CA3AF', margin: '0 0 16px', fontFamily: 'var(--adm-font)' }}>
        {targetType === 'user'
          ? `Wird direkt an „${targetName}" gesendet und automatisch in alle Sprachen übersetzt.`
          : `Wird an alle Mitglieder der Gruppe „${targetName}" gesendet und automatisch übersetzt.`
        }
      </p>
      <Field label="Betreff / Titel">
        <input
          className="adm-input"
          placeholder="z.B. Neue Sicherheitsanweisung verfügbar"
          value={title}
          onChange={e => setTitle(e.target.value)}
          maxLength={120}
          autoFocus
        />
        <p style={{ fontSize: 11, color: '#9CA3AF', margin: '3px 0 0', textAlign: 'right' }}>{title.length}/120</p>
      </Field>
      <Field label={t('adm_description') || 'Nachricht'}>
        <textarea
          className="adm-input"
          style={{ resize: 'vertical', minHeight: 80, lineHeight: 1.55 }}
          placeholder="Nachrichtentext…"
          value={body}
          onChange={e => setBody(e.target.value)}
          maxLength={500}
          rows={3}
        />
        <p style={{ fontSize: 11, color: '#9CA3AF', margin: '3px 0 0', textAlign: 'right' }}>{body.length}/500</p>
      </Field>
      {(title || body) && (
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
          <p style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px', fontFamily: 'var(--adm-font)' }}>Vorschau</p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 18, lineHeight: 1 }}>📢</span>
            <div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111827', fontFamily: 'var(--adm-font)' }}>{title || '…'}</p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280', fontFamily: 'var(--adm-font)', lineHeight: 1.4 }}>{body || '…'}</p>
            </div>
          </div>
          <p style={{ margin: '6px 0 0', fontSize: 11, color: '#FF6407', fontStyle: 'italic', fontFamily: 'var(--adm-font)' }}>✦ Wird automatisch in alle Sprachen übersetzt.</p>
        </div>
      )}
      <div className="adm-modal__footer">
        <button className="adm-btn adm-btn--ghost" onClick={onClose}>{t('adm_cancel')}</button>
        <button className="adm-btn adm-btn--primary" onClick={handleSend} disabled={!canSubmit} style={{ minWidth: 160 }}>
          {sending ? '⏳ Wird übersetzt…' : `📤 Senden an ${icon} ${targetName}`}
        </button>
      </div>
    </Modal>
  );
};

// ─── USERS TAB ───────────────────────────────────────────────────────────────
const groupLabel = (g) => {
  const m = { 0: 'Super Admin', 1: 'Standard', 2: 'Manager', 3: 'Admin' };
  return m[g] ?? `Role ${g}`;
};

// Zeigt Full- vs. Essentials-Lizenztyp als farbiges Badge.
const SeatTypeBadge = ({ type }) => {
  const isFull = type === 'full';
  return (
    <span
      className={`adm-badge ${isFull ? 'adm-badge--full' : 'adm-badge--essentials'}`}
      title={isFull
        ? 'Full-Lizenz: alle Features inkl. VideoBuilder & TTS'
        : 'Essentials-Lizenz: alle Features außer Content-Erstellung'}
      style={{
        background:  isFull ? '#f0fdf4' : '#f8fafc',
        color:       isFull ? '#15803d' : '#475569',
        border:      `1px solid ${isFull ? '#bbf7d0' : '#cbd5e1'}`,
        borderRadius: 999,
        padding:     '2px 8px',
        fontSize:    11,
        fontWeight:  600,
        whiteSpace:  'nowrap',
      }}
    >
      {isFull ? '⭐ Full' : 'Essentials'}
    </span>
  );
};

const UsersTab = ({ toast, allGroups, allClients }) => {
  const { t } = useLanguage();
  const [users, setUsers]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [editUser, setEditUser]         = useState(null);
  const [creating, setCreating]         = useState(false);
  const [assignModal, setAssignModal]   = useState(null);
  const [msgModal, setMsgModal]         = useState(null);
  const [pinModal, setPinModal]         = useState(null); // { pin, qrToken, userName }
  const [qrModal, setQrModal]           = useState(null); // { qrToken, userName }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('/admin/users');
      setUsers(res.data || []);
    } catch (e) { toast(t('adm_toast_load_error'), 'error'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // Count superadmins to guard the last one
  const superAdminCount = users.filter(u => Number(u.group) === 0).length;
  const isSuperAdminProtected = (user) => Number(user.group) === 0 && superAdminCount <= 1;

  const filtered = users.filter(u => {
    const matchSearch = u.name?.toLowerCase().includes(search.toLowerCase()) || String(u.id).includes(search);
    const matchStatus = statusFilter === 'all' ? true
      : statusFilter === 'active' ? u.status === 1 : u.status !== 1;
    return matchSearch && matchStatus;
  });

  const handleDelete = async (user) => {
    try {
      await api('/admin/users/delete', { method: 'POST', body: JSON.stringify({ id: user.id }) });
      toast(t('adm_toast_deleted'), 'success');
      load();
    } catch (e) { toast(t('adm_toast_error'), 'error'); }
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 1 ? 0 : 1;
    setUsers(prev => prev.map(u => u.id === user.id ? { ...u, status: newStatus } : u));
    try {
      await api('/admin/users/update', { method: 'POST', body: JSON.stringify({ id: user.id, status: newStatus }) });
      toast(newStatus === 0 ? t('adm_toast_deactivated') : t('adm_toast_saved'), 'success');
    } catch (e) {
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, status: user.status } : u));
      toast(t('adm_toast_error'), 'error');
    }
  };

  const handleSave = async (formData, isNew) => {
    const endpoint = isNew ? '/admin/users/create' : '/admin/users/update';
    try {
      const result = await api(endpoint, { method: 'POST', body: JSON.stringify(formData) });
      setEditUser(null);
      setCreating(false);
      load();
      // Bei Neuanlage: PIN + QR einmalig anzeigen
      if (isNew && result.pin) {
        setPinModal({ pin: result.pin, qrToken: result.qr_token, userName: formData.name });
      } else {
        toast(isNew ? t('adm_toast_created') : t('adm_toast_saved'), 'success');
      }
    } catch (e) {
      toast(e.message || t('adm_toast_error'), 'error');
    }
  };

  const handleResetPin = async (user) => {
    try {
      const result = await api('/admin/users/reset-pin', {
        method: 'POST',
        body: JSON.stringify({ id: user.id, regenerate_qr: false }),
      });
      if (result.pin) {
        setEditUser(null);
        setPinModal({ pin: result.pin, qrToken: result.qr_token, userName: result.name || user.name });
        load();
      }
    } catch (e) { toast(e.message || t('adm_toast_error'), 'error'); }
  };

  return (
    <div className="adm-tab-content">
      <div className="adm-tab-toolbar">
        <SearchBar value={search} onChange={setSearch} placeholder={`${t('adm_users')} ${t('adm_search_hint')}…`} />
        <StatusFilter value={statusFilter} onChange={setStatusFilter} />
        <button className="adm-btn adm-btn--primary" onClick={() => setCreating(true)}>
          + {t('adm_new_user')}
        </button>
      </div>

      {loading ? (
        <div className="adm-loading"><span className="adm-spinner" /></div>
      ) : (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>{t('adm_name')}</th>
                <th>{t('adm_status')}</th>
                <th>{t('adm_role')}</th>
                <th>Lizenztyp</th>
                <th>{t('adm_groups')}</th>
                <th>XP</th>
                <th>{t('adm_created')}</th>
                <th>{t('adm_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id}>
                  <td className="adm-cell--muted">{u.id}</td>
                  <td>
                    <div className="adm-user-cell">
                      {u.avatar && <img src={u.avatar} alt="" className="adm-avatar-xs" />}
                      <span>{u.name}</span>
                    </div>
                  </td>
                  <td><StatusBadge status={u.status} onToggle={() => handleToggleStatus(u)} /></td>
                  <td><span className="adm-role-chip">{groupLabel(u.group)}</span></td>
                  <td><SeatTypeBadge type={u.seat_type} /></td>
                  <td>
                    <div className="adm-chips">
                      {u.usergroups?.length > 0
                        ? u.usergroups.map(g => <span key={g.id} className="adm-chip">{g.name}</span>)
                        : <span className="adm-chip adm-chip--warn" title={t('adm_no_group_access')}>⚠ {t('adm_no_group')}</span>
                      }
                    </div>
                  </td>
                  <td>{u.xp ?? 0}</td>
                  <td className="adm-cell--muted">{u.created ? u.created.split(' ')[0] : '—'}</td>
                  <td>
                    <div className="adm-action-row">
                      <button className="adm-btn adm-btn--ghost adm-btn--xs" onClick={() => setEditUser(u)}>✏️</button>
                      <button className="adm-btn adm-btn--ghost adm-btn--xs" onClick={() => setAssignModal(u)}>🏷️</button>
                      {u.qr_token && (
                        <button className="adm-btn adm-btn--ghost adm-btn--xs" title="QR-Code anzeigen"
                          onClick={() => setQrModal({ qrToken: u.qr_token, userName: u.name })}>📱</button>
                      )}
                      <button className="adm-btn adm-btn--ghost adm-btn--xs" title="Nachricht senden" onClick={() => setMsgModal({ id: u.id, name: u.name })}>📢</button>
                      <DangerButton
                        label="🗑️"
                        onConfirm={() => handleDelete(u)}
                        disabled={isSuperAdminProtected(u)}
                        disabledTitle={t('adm_last_superadmin_hint')}
                      />
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="adm-table__empty">{t('adm_no_users')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile card list */}
      {!loading && (
        <div className="adm-card-list">
          {filtered.map(u => (
            <div key={u.id} className="adm-card">
              <div className="adm-card__header">
                {u.avatar && <img src={u.avatar} alt="" className="adm-avatar-xs" />}
                <div style={{ flex: 1 }}>
                  <div className="adm-card__title">{u.name}</div>
                  <div className="adm-card__sub">ID {u.id} · {groupLabel(u.group)}</div>
                </div>
                <StatusBadge status={u.status} onToggle={() => handleToggleStatus(u)} />
              </div>
              <div className="adm-card__rows">
                <div className="adm-card__row">
                  <span className="adm-card__label">{t('adm_groups')}</span>
                  <div className="adm-chips">
                    {u.usergroups?.length > 0
                      ? u.usergroups.map(g => <span key={g.id} className="adm-chip">{g.name}</span>)
                      : <span className="adm-chip adm-chip--warn">⚠ {t('adm_no_group')}</span>
                    }
                  </div>
                </div>
                <div className="adm-card__row">
                  <span className="adm-card__label">Lizenztyp</span>
                  <SeatTypeBadge type={u.seat_type} />
                </div>
                <div className="adm-card__row">
                  <span className="adm-card__label">XP</span>
                  <span>{u.xp ?? 0}</span>
                </div>
                {u.created && (
                  <div className="adm-card__row">
                    <span className="adm-card__label">{t('adm_created')}</span>
                    <span className="adm-cell--muted">{u.created.split(' ')[0]}</span>
                  </div>
                )}
              </div>
              {/* QR-Code in der Karte */}
              {u.qr_token && (
                <div style={{ padding: '8px 0 4px', borderTop: '1px solid #F3F4F6', marginTop: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                      Login QR-Code
                    </span>
                    <button
                      className="adm-btn adm-btn--ghost adm-btn--xs"
                      onClick={() => handleResetPin(u)}
                      title="Neuen PIN generieren"
                    >
                      🔄 PIN zurücksetzen
                    </button>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <QRCodeDisplay
                      value={`${window.location.origin}/login?t=${u.qr_token}`}
                      size={140}
                    />
                  </div>
                  <p style={{ fontSize: 10, color: '#9CA3AF', textAlign: 'center', marginTop: 6 }}>
                    Scannen → direkt einloggen
                    {u.must_change_pin === 1 && <span style={{ color: '#f59e0b' }}> · PIN-Änderung ausstehend</span>}
                  </p>
                </div>
              )}
              <div className="adm-card__actions">
                <button className="adm-btn adm-btn--ghost adm-btn--xs" onClick={() => setEditUser(u)}>✏️ {t('adm_edit')}</button>
                <button className="adm-btn adm-btn--ghost adm-btn--xs" onClick={() => setAssignModal(u)}>🏷️ {t('adm_groups')}</button>
                <button className="adm-btn adm-btn--ghost adm-btn--xs" onClick={() => setMsgModal({ id: u.id, name: u.name })}>📢</button>
                <DangerButton
                  label="🗑️"
                  onConfirm={() => handleDelete(u)}
                  disabled={isSuperAdminProtected(u)}
                  disabledTitle="Letzter Super Admin – kann nicht gelöscht werden"
                />
              </div>
            </div>
          ))}
          {filtered.length === 0 && <p className="adm-table__empty">{t('adm_no_users')}</p>}
        </div>
      )}

      {(editUser || creating) && (
        <UserFormModal
          user={editUser}
          isNew={creating}
          allGroups={allGroups}
          allClients={allClients}
          onClose={() => { setEditUser(null); setCreating(false); }}
          onSave={handleSave}
        />
      )}

      {assignModal && (
        <UserGroupAssignModal
          user={assignModal}
          allGroups={allGroups}
          onClose={() => { setAssignModal(null); load(); }}
          toast={toast}
        />
      )}

      {msgModal && (
        <QuickMessageModal
          targetType="user"
          targetId={msgModal.id}
          targetName={msgModal.name}
          onClose={() => setMsgModal(null)}
          toast={toast}
        />
      )}

      {/* QR-Code-Anzeige für bestehende User (qr_token-basiert, kein PIN nötig) */}
      {qrModal && (
        <Modal title={`📱 QR-Code: ${qrModal.userName}`} onClose={() => setQrModal(null)}>
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 20 }}>
              Nutzer kann diesen Code scannen, um sich ohne PIN-Eingabe einzuloggen.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
              <QRCodeDisplay
                value={`${window.location.origin}/login?t=${qrModal.qrToken}`}
                size={200}
              />
            </div>
            <p style={{ fontSize: 11, color: '#9CA3AF' }}>
              {window.location.origin}/login?t={qrModal.qrToken.substring(0, 8)}…
            </p>
          </div>
          <div className="adm-modal__footer">
            <button className="adm-btn adm-btn--ghost" onClick={() => setQrModal(null)}>Schließen</button>
          </div>
        </Modal>
      )}

      {/* PIN + QR einmalige Anzeige nach Erstellung / Reset */}
      {pinModal && (
        <PinCreatedModal
          pin={pinModal.pin}
          qrToken={pinModal.qrToken}
          userName={pinModal.userName}
          onClose={() => setPinModal(null)}
        />
      )}
    </div>
  );
};

// ─── USER FORM MODAL ──────────────────────────────────────────────────────────
// FIX: Mandant-Dropdown ist nur für Super-Admins (group 0) sichtbar.
//      Für alle anderen Admins wird client_id automatisch auf den eigenen
//      Mandanten gesetzt — das Feld wird nicht angezeigt.
//      Backend-seitig erzwingt AdminController::createUser() dasselbe
//      via $this->clientId (JWT-Kontext) — doppelte Absicherung.
const UserFormModal = ({ user, isNew, onClose, onSave, allGroups, allClients }) => {
  const { t } = useLanguage();

  // Eigene Benutzer-Daten aus localStorage lesen
  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem('hejvi_user') || '{}'); } catch { return {}; }
  })();
  const { hasFeature: checkFeature } = useAuth();
  const isSuperAdmin = checkFeature('super_admin');

  // client_id-Default: bei neuem User aus allClients[0] (gefilterte Backend-Liste),
  // Fallback auf storedUser.client_id, letzter Fallback auf 1.
  const defaultClientId = user?.client_id
    ?? allClients?.[0]?.id
    ?? storedUser?.client_id
    ?? storedUser?.clientId
    ?? 1;

  const [form, setForm] = useState({
    id: user?.id || '',
    name: user?.name || '',
    status: user?.status ?? 1,
    group: user?.group ?? 1,
    preferred_language: user?.preferred_language ?? 1,
    client_id: defaultClientId,
    seat_type: user?.seat_type ?? 'essentials',
    must_change_pin: 0, // nur bei Neuanlage relevant
  });

  const [showNoGroupWarning, setShowNoGroupWarning] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const doSave = (payload) => {
    const p = { ...payload };
    if (!p.pin) delete p.pin;
    onSave(p, isNew);
  };

  const handleSubmit = () => {
    if (isNew) {
      // PIN wird vom System generiert — kein clientseitiger PIN-Check nötig.
      // Gibt es eine aktive Default-Gruppe? Backend weist sie automatisch zu.
      const hasDefaultGroup = (allGroups || []).some(g => g.is_default === 1 && g.status === 1);
      if (hasDefaultGroup) {
        doSave(form);
        return;
      }
      // Edge-Case: noch keine Default-Gruppe vorhanden
      setShowNoGroupWarning(true);
      return;
    }
    doSave(form);
  };

  const langOptions   = Object.entries(LANG_MAP).map(([id, { label }]) => ({ id: Number(id), label }));
  const clientOptions = (allClients || []).map(c => ({ id: c.id, label: c.name }));

  return (
    <Modal title={isNew ? t('adm_new_user') : `${t('adm_edit_user')}: ${user.name}`} onClose={onClose}>

      {showNoGroupWarning ? (
        <div>
          <div className="adm-warning-banner">
            <strong>⚠️ {t('adm_no_group_warn_title')}</strong>
            <p style={{ marginTop: '0.4rem', marginBottom: 0 }}>
              {t('adm_no_group_warn_body')}
            </p>
          </div>
          <div className="adm-modal__footer">
            <button className="adm-btn adm-btn--ghost" onClick={() => setShowNoGroupWarning(false)}>← {t('adm_back')}</button>
            <button className="adm-btn adm-btn--primary" onClick={() => doSave(form)}>{t('adm_create')}</button>
          </div>
        </div>
      ) : (
        <>
          <Field label={`${t('adm_name')} *`}>
            <input className="adm-input" value={form.name} onChange={e => set('name', e.target.value)} />
          </Field>
          {isNew ? (
            /* Neuanlage: PIN wird vom System generiert */
            <div style={{
              background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8,
              padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#166534',
            }}>
              🔐 PIN wird automatisch generiert und nach der Erstellung einmalig angezeigt.
            </div>
          ) : (
            /* Bearbeiten: PIN-Reset-Hinweis */
            <div style={{
              background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8,
              padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#374151',
            }}>
              Zum Zurücksetzen des PINs: Speichern schließen und 🔄 <strong>PIN zurücksetzen</strong> in der Nutzerkarte verwenden.
            </div>
          )}
          <div className="adm-form-row">
            <Field label={t('adm_status')}>
              <select className="adm-input" value={form.status} onChange={e => set('status', Number(e.target.value))}>
                <option value={1}>{t('adm_active')}</option>
                <option value={0}>{t('adm_inactive')}</option>
              </select>
            </Field>
            <Field label={t('adm_role')}>
              <select className="adm-input" value={form.group} onChange={e => set('group', Number(e.target.value))}>
                <option value={1}>Standard</option>
                <option value={3}>Admin</option>
                <option value={2}>Manager</option>
                {isSuperAdmin && <option value={0}>Super Admin</option>}
              </select>
            </Field>
          </div>

          {/* Lizenztyp: steuert ob User VideoBuilder/TTS nutzen darf */}
          <Field
            label="Lizenztyp"
            hint="Full = alle Features inkl. Content-Erstellung · Essentials = ohne VideoBuilder & TTS"
          >
            <select
              className="adm-input"
              value={form.seat_type}
              onChange={e => set('seat_type', e.target.value)}
            >
              <option value="essentials">Essentials – alle Features außer Content-Erstellung</option>
              <option value="full">⭐ Full – inkl. VideoBuilder & TTS</option>
            </select>
          </Field>
          {isNew && (
            <Field
              label="PIN-Änderung bei erstem Login erzwingen"
              hint="Der Nutzer muss nach dem ersten Login einen eigenen PIN setzen."
            >
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '6px 0' }}>
                <input
                  type="checkbox"
                  checked={form.must_change_pin === 1}
                  onChange={e => set('must_change_pin', e.target.checked ? 1 : 0)}
                  style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#FF6407' }}
                />
                <span style={{ fontSize: 13 }}>Aktivieren (optional)</span>
              </label>
            </Field>
          )}
          <div className="adm-form-row">
            <Field label={t('adm_lang_id') || 'Sprache'}>
              <select className="adm-input" value={form.preferred_language} onChange={e => set('preferred_language', Number(e.target.value))}>
                {langOptions.map(l => (
                  <option key={l.id} value={l.id}>{l.label}</option>
                ))}
              </select>
            </Field>

            {/* Mandant-Feld: nur für Super-Admins sichtbar (group === 0).
                Für alle anderen Admins wird client_id serverseitig auf den
                eigenen Mandanten erzwungen — kein Dropdown nötig. */}
            {isSuperAdmin ? (
              <Field label={t('adm_client_id') || 'Mandant'}>
                <select className="adm-input" value={form.client_id} onChange={e => set('client_id', Number(e.target.value))}>
                  {clientOptions.length > 0
                    ? clientOptions.map(c => <option key={c.id} value={c.id}>{c.label}</option>)
                    : <option value={form.client_id}>{form.client_id}</option>
                  }
                </select>
              </Field>
            ) : (
              // Kein sichtbares Feld — client_id wird vom Backend (JWT-Kontext) gesetzt
              <input type="hidden" value={form.client_id} readOnly />
            )}
          </div>
          <div className="adm-modal__footer">
            <button className="adm-btn adm-btn--ghost" onClick={onClose}>{t('adm_cancel')}</button>
            <button className="adm-btn adm-btn--primary" onClick={handleSubmit}>
              {isNew ? t('adm_create') : t('adm_save')}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

const UserGroupAssignModal = ({ user, allGroups, onClose, toast }) => {
  const { t } = useLanguage();
  const [current, setCurrent] = useState(user.usergroups?.map(g => g.id) || []);
  const [busy, setBusy] = useState(false);

  const toggle = async (groupId) => {
    setBusy(true);
    const isAssigned = current.includes(groupId);
    const endpoint = isAssigned ? '/admin/usergroups/remove-user' : '/admin/usergroups/assign-user';
    try {
      await api(endpoint, { method: 'POST', body: JSON.stringify({ user_id: user.id, group_id: groupId }) });
      setCurrent(prev => isAssigned ? prev.filter(id => id !== groupId) : [...prev, groupId]);
      toast(isAssigned ? t('adm_toast_removed_from_group') : t('adm_toast_added_to_group'), 'success');
    } catch (e) { toast(t('adm_toast_error'), 'error'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`${t('adm_groups_for')}: ${user.name}`} onClose={onClose}>
      <p className="adm-modal__sub">{t('adm_groups_assign_hint')}</p>
      {current.length === 0 && (
        <div className="adm-warning-banner">
          ⚠️ {t('adm_no_group_assign_warn')}
        </div>
      )}
      <div className="adm-group-toggle-list">
        {allGroups.map(g => {
          const active = current.includes(g.id);
          return (
            <button
              key={g.id}
              className={`adm-group-toggle ${active ? 'adm-group-toggle--active' : ''}`}
              onClick={() => toggle(g.id)}
              disabled={busy}
            >
              <span className="adm-group-toggle__name">{g.name}</span>
              <span className="adm-group-toggle__state">{active ? '✓ ' + t('adm_assigned') : '+ ' + t('adm_add')}</span>
            </button>
          );
        })}
        {allGroups.length === 0 && <p className="adm-cell--muted">{t('adm_no_groups')}</p>}
      </div>
      <div className="adm-modal__footer">
        <button className="adm-btn adm-btn--primary" onClick={onClose}>{t('adm_done')}</button>
      </div>
    </Modal>
  );
};

// ─── USERGROUPS TAB ──────────────────────────────────────────────────────────
const UsergroupsTab = ({ toast, allGroups, reloadGroups, allUsers }) => {
  const { t } = useLanguage();
  const [search, setSearch]               = useState('');
  const [statusFilter, setStatusFilter]   = useState('active');
  const [editGroup, setEditGroup]         = useState(null);
  const [creating, setCreating]           = useState(false);
  const [contentModalId, setContentModalId] = useState(null);
  const [userModalId, setUserModalId]       = useState(null);
  const [msgModal, setMsgModal]             = useState(null);

  const filtered = allGroups.filter(g => {
    const matchSearch = g.name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' ? true
      : statusFilter === 'active' ? g.status === 1 : g.status !== 1;
    return matchSearch && matchStatus;
  });

  const handleDelete = async (id) => {
    try {
      await api('/admin/usergroups/delete', { method: 'POST', body: JSON.stringify({ id }) });
      toast(t('adm_toast_deleted'), 'success');
      reloadGroups();
    } catch (e) { toast(t('adm_toast_error'), 'error'); }
  };

  const handleSave = async (formData, isNew) => {
    const endpoint = isNew ? '/admin/usergroups/create' : '/admin/usergroups/update';
    try {
      await api(endpoint, { method: 'POST', body: JSON.stringify(formData) });
      toast(isNew ? t('adm_toast_created') : t('adm_toast_saved'), 'success');
      setEditGroup(null);
      setCreating(false);
      reloadGroups();
    } catch (e) { toast(e.message || t('adm_toast_error'), 'error'); }
  };

  return (
    <div className="adm-tab-content">
      <div className="adm-tab-toolbar">
        <SearchBar value={search} onChange={setSearch} placeholder={`${t('adm_groups')} ${t('adm_search_hint')}…`} />
        <StatusFilter value={statusFilter} onChange={setStatusFilter} />
        <button className="adm-btn adm-btn--primary" onClick={() => setCreating(true)}>
          + {t('adm_new_group')}
        </button>
      </div>

      <div className="adm-table-wrap">
        <table className="adm-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>{t('adm_name')} (DE)</th>
              <th>{t('adm_name')} (EN)</th>
              <th>{t('adm_status')}</th>
              <th>{t('adm_members')}</th>
              <th>{t('adm_content')}</th>
              <th>{t('adm_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(g => (
              <tr key={g.id}>
                <td className="adm-cell--muted">{g.id}</td>
                <td><strong>{g.name}</strong></td>
                <td className="adm-cell--muted">{g.name_en || '—'}</td>
                <td><StatusBadge status={g.status} /></td>
                <td><span className="adm-count-badge">{g.member_count}</span></td>
                <td><span className="adm-count-badge">{g.content?.length ?? 0}</span></td>
                <td>
                  <div className="adm-action-row">
                    <button className="adm-btn adm-btn--ghost adm-btn--xs" onClick={() => setEditGroup(g)}>✏️</button>
                    <button className="adm-btn adm-btn--ghost adm-btn--xs" onClick={() => setContentModalId(g.id)}>📚</button>
                    <button className="adm-btn adm-btn--ghost adm-btn--xs" onClick={() => setUserModalId(g.id)}>👥</button>
                    <button className="adm-btn adm-btn--ghost adm-btn--xs" title="Nachricht an Gruppe" onClick={() => setMsgModal({ id: g.id, name: g.name })}>📢</button>
                    <DangerButton label="🗑️" onConfirm={() => handleDelete(g.id)} />
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="adm-table__empty">{t('adm_no_groups')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="adm-card-list">
        {filtered.map(g => (
          <div key={g.id} className="adm-card">
            <div className="adm-card__header">
              <div style={{ flex: 1 }}>
                <div className="adm-card__title">{g.name}</div>
                {g.name_en && <div className="adm-card__sub">{g.name_en}</div>}
              </div>
              <StatusBadge status={g.status} />
            </div>
            <div className="adm-card__rows">
              <div className="adm-card__row">
                <span className="adm-card__label">{t('adm_members')}</span>
                <span className="adm-count-badge">{g.member_count}</span>
              </div>
              <div className="adm-card__row">
                <span className="adm-card__label">{t('adm_content')}</span>
                <span className="adm-count-badge">{g.content?.length ?? 0}</span>
              </div>
            </div>
            <div className="adm-card__actions">
              <button className="adm-btn adm-btn--ghost adm-btn--xs" onClick={() => setEditGroup(g)}>✏️ {t('adm_edit')}</button>
              <button className="adm-btn adm-btn--ghost adm-btn--xs" onClick={() => setContentModalId(g.id)}>📚 {t('adm_content')}</button>
              <button className="adm-btn adm-btn--ghost adm-btn--xs" onClick={() => setUserModalId(g.id)}>👥 {t('adm_members')}</button>
              <button className="adm-btn adm-btn--ghost adm-btn--xs" onClick={() => setMsgModal({ id: g.id, name: g.name })}>📢</button>
              <DangerButton label="🗑️" onConfirm={() => handleDelete(g.id)} />
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="adm-table__empty">{t('adm_no_groups')}</p>}
      </div>

      {(editGroup || creating) && (
        <GroupFormModal
          group={editGroup}
          isNew={creating}
          onClose={() => { setEditGroup(null); setCreating(false); }}
          onSave={handleSave}
        />
      )}

      {contentModalId && (() => {
        const liveGroup = allGroups.find(g => g.id === contentModalId);
        return liveGroup ? (
          <GroupContentModal
            group={liveGroup}
            onClose={() => setContentModalId(null)}
            onToggle={(elementId, elementType, assigned) => {
              reloadGroups();
            }}
            toast={toast}
          />
        ) : null;
      })()}

      {userModalId && (() => {
        const liveGroup = allGroups.find(g => g.id === userModalId);
        return liveGroup ? (
          <GroupUserModal
            group={liveGroup}
            onClose={() => { setUserModalId(null); reloadGroups(); }}
            onMemberChange={() => reloadGroups()}
            toast={toast}
          />
        ) : null;
      })()}

      {msgModal && (
        <QuickMessageModal
          targetType="group"
          targetId={msgModal.id}
          targetName={msgModal.name}
          onClose={() => setMsgModal(null)}
          toast={toast}
        />
      )}
    </div>
  );
};

const GroupFormModal = ({ group, isNew, onClose, onSave }) => {
  const { t, currentLanguage } = useLanguage();
  const [saving, setSaving] = useState(false);

  const langCode = currentLanguage || 'de';
  const nameField = langCode === 'de' ? 'name' : `name_${langCode}`;
  const descField = langCode === 'de' ? 'description' : `description_${langCode}`;

  const langEntry = Object.entries(LANG_MAP).find(([, v]) => v.code === langCode);
  const langLabel = langEntry ? LANG_MAP[langEntry[0]].label : 'DE';

  const [form, setForm] = useState({
    id: group?.id || '',
    name: group?.[nameField] || group?.name || '',
    description: group?.[descField] || group?.description || '',
    name_en: group?.name_en || '',
    status: group?.status ?? 1,
    rights_level: group?.rights_level ?? 0,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const modalTitle = isNew
    ? t('adm_new_group')
    : `${t('adm_edit_group')}: ${group?.[nameField] || group?.name || ''}`;

  return (
    <Modal title={modalTitle} onClose={onClose}>
      <div className="adm-form-row">
        <Field label={`${t('adm_name')} (${langLabel}) *`}>
          <input className="adm-input" value={form.name} onChange={e => set('name', e.target.value)} />
        </Field>
        <Field label={`${t('adm_name')} (English)`} hint={t('adm_en_readonly_hint')}>
          <input className="adm-input adm-input--readonly" value={form.name_en} readOnly tabIndex={-1} />
        </Field>
      </div>
      <Field label={`${t('adm_description')} (${langLabel})`}>
        <textarea className="adm-input adm-textarea" value={form.description} onChange={e => set('description', e.target.value)} rows={3} />
      </Field>
      <Field label={t('adm_status')}>
        <select className="adm-input" value={form.status} onChange={e => set('status', Number(e.target.value))}>
          <option value={1}>{t('adm_active')}</option>
          <option value={0}>{t('adm_inactive')}</option>
        </select>
      </Field>
      <p className="adm-field__hint">{t('adm_auto_translate_hint')}</p>
      <div className="adm-modal__footer">
        <button className="adm-btn adm-btn--ghost" onClick={onClose} disabled={saving}>{t('adm_cancel')}</button>
        <button className="adm-btn adm-btn--primary" disabled={saving} onClick={async () => {
          setSaving(true);
          await onSave({ ...form, source_lang: langCode }, isNew);
          setSaving(false);
        }}>
          {saving ? t('adm_translating') : isNew ? t('adm_create') : t('adm_save_translate')}
        </button>
      </div>
    </Modal>
  );
};

// ─── CONTENT in GROUP MODAL ──────────────────────────────────────────────────
const GroupContentModal = ({ group, onClose, onToggle, toast }) => {
  const { t, currentLanguage } = useLanguage();
  const [elements, setElements]         = useState([]);
  const [collections, setCollections]   = useState([]);
  const [tab, setTab]                   = useState('collections');
  const [search, setSearch]             = useState('');
  const [onlyAssigned, setOnlyAssigned] = useState(false);
  const [busyId, setBusyId]             = useState(null);

  const assignedElemIds = (group.content || []).filter(c => String(c.element_type) === '0').map(c => Number(c.element_id));
  const assignedCollIds = (group.content || []).filter(c => String(c.element_type) === '1').map(c => Number(c.element_id));
  const [localElemIds, setLocalElemIds] = useState(assignedElemIds);
  const [localCollIds, setLocalCollIds] = useState(assignedCollIds);

  const [loadingContent, setLoadingContent] = useState(false);
  useEffect(() => {
    const loadContent = async () => {
      setLoadingContent(true);
      try {
        const [eRes, cRes] = await Promise.all([
          api('/admin/elements'),
          api('/admin/collections'),
        ]);
        setElements(eRes.data || []);
        setCollections(cRes.data || []);
      } catch (e) { toast(t('adm_toast_load_error'), 'error'); }
      finally { setLoadingContent(false); }
    };
    loadContent();
  }, [toast]);

  const toggle = async (itemId, itemType, isAssigned, setLocal) => {
    setBusyId(itemId);
    const endpoint = isAssigned ? '/admin/content/remove-group' : '/admin/content/assign-group';
    try {
      await api(endpoint, { method: 'POST', body: JSON.stringify({
        element_id: itemId,
        group_id: group.id,
        element_type: itemType,
      })});
      setLocal(prev => isAssigned ? prev.filter(id => id !== itemId) : [...prev, itemId]);
      toast(isAssigned ? t('adm_toast_content_removed') : t('adm_toast_content_assigned'), 'success');
      if (onToggle) onToggle(itemId, itemType, !isAssigned);
    } catch (e) { toast(t('adm_toast_error'), 'error'); }
    finally { setBusyId(null); }
  };

  const activeItems = tab === 'elements' ? elements : collections;
  const localIds    = tab === 'elements' ? localElemIds : localCollIds;
  const setLocal    = tab === 'elements' ? setLocalElemIds : setLocalCollIds;
  const itemType    = tab === 'elements' ? 0 : 1;

  const filteredItems = activeItems.filter(item => {
    const localName = getLocalName(item, currentLanguage);
    const matchSearch = localName.toLowerCase().includes(search.toLowerCase()) ||
      (item.name_en || '').toLowerCase().includes(search.toLowerCase());
    const matchAssigned = !onlyAssigned || localIds.includes(item.id);
    return matchSearch && matchAssigned;
  });

  return (
    <Modal title={`${t('adm_content_for_group')}: ${group.name}`} onClose={onClose} wide>
      <div className="adm-subtabs">
        <button className={`adm-subtab ${tab === 'collections' ? 'adm-subtab--active' : ''}`}
          onClick={() => { setTab('collections'); setSearch(''); }}>
          📚 {t('adm_topics')} ({localCollIds.length})
        </button>
        <button className={`adm-subtab ${tab === 'elements' ? 'adm-subtab--active' : ''}`}
          onClick={() => { setTab('elements'); setSearch(''); }}>
          🎬 {t('adm_videos')} ({localElemIds.length})
        </button>
      </div>

      <div className="adm-tab-toolbar adm-tab-toolbar--compact">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder={`${tab === 'elements' ? t('adm_video_sg') : t('adm_topic_sg')} ${t('adm_search_hint')}…`}
        />
        <button
          className={`adm-toggle-btn ${onlyAssigned ? 'adm-toggle-btn--active' : ''}`}
          onClick={() => setOnlyAssigned(v => !v)}
          type="button"
        >
          {onlyAssigned ? '✓ Nur zugeordnete' : '☐ Nur zugeordnete'}
        </button>
      </div>

      {loadingContent ? (
        <div className="adm-loading"><span className="adm-spinner" /></div>
      ) : (
        <div className="adm-content-toggle-list">
          {filteredItems.map(item => {
            const isAssigned = localIds.includes(item.id);
            const isBusy = busyId === item.id;
            const displayName = getLocalName(item, currentLanguage);
            const displayDesc = getLocalDesc(item, currentLanguage);
            return (
              <div key={item.id} className={`adm-content-row ${isAssigned ? 'adm-content-row--active' : ''}`}>
                {item.url_thumbnail && (
                  <img src={item.url_thumbnail} alt="" className="adm-content-thumb" />
                )}
                <div className="adm-content-info">
                  <span className="adm-content-name">{displayName}</span>
                  <ContentTypeBadge item={item} />
                  {item.name_en && item.name_en !== displayName && (
                    <span className="adm-content-sub">{item.name_en}</span>
                  )}
                  {displayDesc && (
                    <span className="adm-content-desc">{truncate(displayDesc)}</span>
                  )}
                </div>
                <StatusBadge status={item.status} />
                <button
                  className={`adm-btn adm-btn--xs ${isAssigned ? 'adm-btn--danger' : 'adm-btn--primary'}`}
                  onClick={() => toggle(item.id, itemType, isAssigned, setLocal)}
                  disabled={isBusy || busyId !== null}
                >
                  {isBusy ? '…' : isAssigned ? '– ' + t('adm_remove') : '+ ' + t('adm_assign')}
                </button>
              </div>
            );
          })}
          {filteredItems.length === 0 && <p className="adm-table__empty">{t('adm_no_content')}</p>}
        </div>
      )}

      <div className="adm-modal__footer">
        <button className="adm-btn adm-btn--primary" onClick={onClose}>{t('adm_done')}</button>
      </div>
    </Modal>
  );
};

// ─── GROUP USER MODAL ────────────────────────────────────────────────────────
const GroupUserModal = ({ group, onClose, onMemberChange, toast }) => {
  const { t } = useLanguage();
  const [users, setUsers]           = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [search, setSearch]         = useState('');
  const [onlyMembers, setOnlyMembers] = useState(false);
  const [busyId, setBusyId]         = useState(null);

  const [memberIds, setMemberIds] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoadingUsers(true);
      try {
        const res = await api('/admin/users');
        const data = res.data || [];
        setUsers(data);
        setMemberIds(
          data
            .filter(u => (u.usergroups || []).some(ug => ug.id === group.id))
            .map(u => u.id)
        );
      } catch (e) { toast(t('adm_toast_load_error'), 'error'); }
      finally { setLoadingUsers(false); }
    };
    load();
  }, [group.id]);

  const toggle = async (userId) => {
    const isAssigned = memberIds.includes(userId);
    const endpoint = isAssigned ? '/admin/usergroups/remove-user' : '/admin/usergroups/assign-user';
    setBusyId(userId);
    try {
      await api(endpoint, { method: 'POST', body: JSON.stringify({ user_id: userId, group_id: group.id }) });
      setMemberIds(prev => isAssigned ? prev.filter(id => id !== userId) : [...prev, userId]);
      toast(isAssigned ? t('adm_toast_removed_from_group') : t('adm_toast_added_to_group'), 'success');
      if (onMemberChange) onMemberChange();
    } catch (e) { toast(t('adm_toast_error'), 'error'); }
    finally { setBusyId(null); }
  };

  const filteredUsers = users.filter(u => {
    const matchSearch = u.name?.toLowerCase().includes(search.toLowerCase()) ||
      String(u.id).includes(search);
    const matchOnly = !onlyMembers || memberIds.includes(u.id);
    return matchSearch && matchOnly;
  });

  return (
    <Modal title={`👥 ${t('adm_members')}: ${group.name}`} onClose={onClose} wide>
      <div className="adm-tab-toolbar adm-tab-toolbar--compact">
        <SearchBar value={search} onChange={setSearch} placeholder={`${t('adm_users')} ${t('adm_search_hint')}…`} />
        <button
          className={`adm-toggle-btn ${onlyMembers ? 'adm-toggle-btn--active' : ''}`}
          onClick={() => setOnlyMembers(v => !v)}
          type="button"
        >
          {onlyMembers ? '✓ Nur Mitglieder' : '☐ Nur Mitglieder'}
        </button>
        <span className="adm-cell--muted" style={{ fontSize: '0.82rem' }}>
          {memberIds.length} Mitglied{memberIds.length !== 1 ? 'er' : ''}
        </span>
      </div>

      {loadingUsers ? (
        <div className="adm-loading"><span className="adm-spinner" /></div>
      ) : (
        <div className="adm-content-toggle-list">
          {filteredUsers.map(u => {
            const isAssigned = memberIds.includes(u.id);
            const isBusy = busyId === u.id;
            return (
              <div key={u.id} className={`adm-content-row ${isAssigned ? 'adm-content-row--active' : ''}`}>
                {u.avatar
                  ? <img src={u.avatar} alt="" className="adm-content-thumb" style={{ borderRadius: '50%' }} />
                  : <div className="adm-content-thumb adm-thumb--placeholder" style={{ borderRadius: '50%' }} />
                }
                <div className="adm-content-info">
                  <span className="adm-content-name">{u.name}</span>
                  <span className="adm-content-sub">{groupLabel(u.group)} · ID {u.id}</span>
                </div>
                <StatusBadge status={u.status} />
                <button
                  className={`adm-btn adm-btn--xs ${isAssigned ? 'adm-btn--danger' : 'adm-btn--primary'}`}
                  onClick={() => toggle(u.id)}
                  disabled={isBusy || busyId !== null}
                >
                  {isBusy ? '…' : isAssigned ? '– ' + t('adm_remove') : '+ ' + t('adm_assign')}
                </button>
              </div>
            );
          })}
          {filteredUsers.length === 0 && <p className="adm-table__empty">{t('adm_no_users')}</p>}
        </div>
      )}

      <div className="adm-modal__footer">
        <button className="adm-btn adm-btn--primary" onClick={onClose}>{t('adm_done')}</button>
      </div>
    </Modal>
  );
};

// ─── ELEMENTS TAB ─────────────────────────────────────────────────────────────
const ElementsTab = ({ toast, allGroups }) => {
  const { hasFeature } = useAuth();
  const { t, currentLanguage } = useLanguage();
  const [items, setItems]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [editItem, setEditItem]         = useState(null);
  const [assignItem, setAssignItem]     = useState(null);
  const [collectionsModal, setCollectionsModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('/admin/elements');
      setItems(res.data || []);
    } catch (e) { toast(t('adm_toast_load_error'), 'error'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(i => {
    const localName = getLocalName(i, currentLanguage);
    const matchSearch = localName.toLowerCase().includes(search.toLowerCase()) ||
      (i.name_en || '').toLowerCase().includes(search.toLowerCase()) ||
      String(i.id).includes(search);
    const matchStatus = statusFilter === 'all' ? true
      : statusFilter === 'active' ? i.status === 1 : i.status !== 1;
    return matchSearch && matchStatus;
  });

  const handleDelete = async (id) => {
    try {
      await api('/admin/elements/delete', { method: 'POST', body: JSON.stringify({ id }) });
      toast(t('adm_toast_deactivated'), 'success');
      load();
    } catch (e) { toast(t('adm_toast_error'), 'error'); }
  };

  const handleToggle = async (item) => {
    const newStatus = item.status === 1 ? 0 : 1;
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: newStatus } : i));
    try {
      await api('/admin/elements/update', { method: 'POST', body: JSON.stringify({ id: item.id, status: newStatus }) });
      toast(newStatus === 0 ? t('adm_toast_deactivated') : t('adm_toast_saved'), 'success');
    } catch (e) {
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: item.status } : i));
      toast(t('adm_toast_error'), 'error');
    }
  };

  const handleSave = async (formData) => {
    try {
      await api('/admin/elements/update', { method: 'POST', body: JSON.stringify(formData) });
      toast(t('adm_toast_saved'), 'success');
      setEditItem(null);
      load();
    } catch (e) { toast(e.message || t('adm_toast_error'), 'error'); }
  };

  return (
    <ContentListTab
      items={filtered}
      loading={loading}
      search={search}
      onSearch={setSearch}
      statusFilter={statusFilter}
      onStatusFilter={setStatusFilter}
      onEdit={setEditItem}
      onDelete={handleDelete}
      onAssign={setAssignItem}
      canAssignGroups={hasFeature('group_management')}
      onManageRelations={setCollectionsModal}
      manageRelationsLabel="📚"
      manageRelationsTitle="Zugeordnete Inhalte anzeigen"
      allGroups={allGroups}
      onToggle={handleToggle}
      typeLabel={t('adm_video_sg')}
    >
      {editItem && (
        <ContentFormModal
          item={editItem}
          isCollection={false}
          onClose={() => setEditItem(null)}
          onSave={handleSave}
        />
      )}
      {assignItem && (
        <SingleContentGroupModal
          item={assignItem}
          itemType={0}
          allGroups={allGroups}
          onClose={() => { setAssignItem(null); load(); }}
          toast={toast}
        />
      )}
      {collectionsModal && (
        <ElementCollectionsModal
          element={collectionsModal}
          toast={toast}
          onClose={() => setCollectionsModal(null)}
        />
      )}
    </ContentListTab>
  );
};

// ─── COLLECTIONS TAB ─────────────────────────────────────────────────────────
const CollectionsTab = ({ toast, allGroups }) => {
  const { hasFeature } = useAuth();
  const { t, currentLanguage } = useLanguage();
  const [items, setItems]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [editItem, setEditItem]         = useState(null);
  const [assignItem, setAssignItem]     = useState(null);
  const [elementsModal, setElementsModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('/admin/collections');
      setItems(res.data || []);
    } catch (e) { toast(t('adm_toast_load_error'), 'error'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(i => {
    const localName = getLocalName(i, currentLanguage);
    const matchSearch = localName.toLowerCase().includes(search.toLowerCase()) ||
      (i.name_en || '').toLowerCase().includes(search.toLowerCase()) ||
      String(i.id).includes(search);
    const matchStatus = statusFilter === 'all' ? true
      : statusFilter === 'active' ? i.status === 1 : i.status !== 1;
    return matchSearch && matchStatus;
  });

  const handleDelete = async (id) => {
    try {
      await api('/admin/collections/delete', { method: 'POST', body: JSON.stringify({ id }) });
      toast(t('adm_toast_deactivated'), 'success');
      load();
    } catch (e) { toast(t('adm_toast_error'), 'error'); }
  };

  const handleToggle = async (item) => {
    const newStatus = item.status === 1 ? 0 : 1;
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: newStatus } : i));
    try {
      await api('/admin/collections/update', { method: 'POST', body: JSON.stringify({ id: item.id, status: newStatus }) });
      toast(newStatus === 0 ? t('adm_toast_deactivated') : t('adm_toast_saved'), 'success');
    } catch (e) {
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: item.status } : i));
      toast(t('adm_toast_error'), 'error');
    }
  };

  const handleSave = async (formData) => {
    try {
      await api('/admin/collections/update', { method: 'POST', body: JSON.stringify(formData) });
      toast(t('adm_toast_saved'), 'success');
      setEditItem(null);
      load();
    } catch (e) { toast(e.message || t('adm_toast_error'), 'error'); }
  };

  return (
    <ContentListTab
      items={filtered}
      loading={loading}
      search={search}
      onSearch={setSearch}
      statusFilter={statusFilter}
      onStatusFilter={setStatusFilter}
      onEdit={setEditItem}
      onDelete={handleDelete}
      onAssign={setAssignItem}
      canAssignGroups={hasFeature('group_management')}
      onManageRelations={setElementsModal}
      manageRelationsLabel="🎬"
      manageRelationsTitle="Videos verwalten"
      allGroups={allGroups}
      onToggle={handleToggle}
      typeLabel={t('adm_topic_sg')}
    >
      {editItem && (
        <ContentFormModal
          item={editItem}
          isCollection={true}
          onClose={() => setEditItem(null)}
          onSave={handleSave}
        />
      )}
      {assignItem && (
        <SingleContentGroupModal
          item={assignItem}
          itemType={1}
          allGroups={allGroups}
          onClose={() => { setAssignItem(null); load(); }}
          toast={toast}
        />
      )}
      {elementsModal && (
        <CollectionElementsModal
          collection={elementsModal}
          toast={toast}
          onClose={() => setElementsModal(null)}
        />
      )}
    </ContentListTab>
  );
};

// ─── Content Type Badge ──────────────────────────────────────────────────────
const ContentTypeBadge = ({ item }) => {
  const { t } = useLanguage();
  const hasDoc   = item.has_document;
  const hasVideo = item.has_video_chunks;
  if (!hasDoc && !hasVideo) return null;
  const tags = [];
  if (hasVideo) tags.push({ label: 'Video', color: '#6D28D9', bg: '#F5F3FF', border: '#DDD6FE', titleKey: 'adm_open_video' });
  if (hasDoc)   tags.push({ label: 'Dok.',  color: '#0369A1', bg: '#F0F9FF', border: '#BAE6FD', titleKey: 'adm_open_doc'   });
  const href = item.hash_id ? `/element/${item.hash_id}` : null;
  return (
    <span style={{ display: 'inline-flex', gap: 3 }}>
      {tags.map(tag => href ? (
        <a
          key={tag.label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title={t(tag.titleKey)}
          onClick={e => e.stopPropagation()}
          style={{
            fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
            color: tag.color, background: tag.bg, border: '1px solid ' + tag.border,
            letterSpacing: '0.02em', textDecoration: 'none', cursor: 'pointer',
            transition: 'filter 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.filter = 'brightness(0.88)'}
          onMouseLeave={e => e.currentTarget.style.filter = ''}
        >{tag.label} ↗</a>
      ) : (
        <span key={tag.label} style={{
          fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
          color: tag.color, background: tag.bg, border: '1px solid ' + tag.border,
          letterSpacing: '0.02em',
        }}>{tag.label}</span>
      ))}
    </span>
  );
};

const ContentListTab = ({ items, loading, search, onSearch, statusFilter, onStatusFilter, onEdit, onDelete, onAssign, onToggle, typeLabel, children, allGroups, onManageRelations, manageRelationsLabel, manageRelationsTitle, canAssignGroups = false }) => {
  const { t, currentLanguage } = useLanguage();
  const groupName = (gid) => allGroups?.find(g => g.id === gid)?.name || `#${gid}`;

  return (
    <div className="adm-tab-content">
      <div className="adm-tab-toolbar">
        <SearchBar value={search} onChange={onSearch} placeholder={`${typeLabel} suchen…`} />
        <StatusFilter value={statusFilter} onChange={onStatusFilter} />
      </div>

      {loading ? (
        <div className="adm-loading"><span className="adm-spinner" /></div>
      ) : (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>{t('adm_preview')}</th>
                <th>{t('adm_name')}</th>
                <th>{t('adm_status')}</th>
                <th>{t('adm_groups')}</th>
                <th>{t('adm_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => {
                const localName = getLocalName(item, currentLanguage);
                const localDesc = getLocalDesc(item, currentLanguage);
                return (
                  <tr key={item.id}>
                    <td>
                      {item.url_thumbnail
                        ? <img src={item.url_thumbnail} alt="" className="adm-thumb" />
                        : <div className="adm-thumb adm-thumb--placeholder" />
                      }
                    </td>
                    <td>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span className="adm-content-name">{localName}</span>
                          <ContentTypeBadge item={item} />
                        </div>
                        {item.name_en && item.name_en !== localName && (
                          <div className="adm-cell--muted" style={{ fontSize: '0.78rem' }}>{item.name_en}</div>
                        )}
                        {localDesc && (
                          <div className="adm-content-desc adm-content-desc--table">{truncate(localDesc)}</div>
                        )}
                      </div>
                    </td>
                    <td><StatusBadge status={item.status} onToggle={onToggle ? () => onToggle(item) : undefined} /></td>
                    <td>
                      <div className="adm-chips">
                        {item.group_ids?.length > 0
                          ? item.group_ids.map(gid => <span key={gid} className="adm-chip">{groupName(gid)}</span>)
                          : <span className="adm-cell--muted">—</span>
                        }
                      </div>
                    </td>
                    <td>
                      <div className="adm-action-row">
                        <button className="adm-btn adm-btn--ghost adm-btn--xs" onClick={() => onEdit(item)}>✏️</button>
                        {canAssignGroups && <button className="adm-btn adm-btn--ghost adm-btn--xs" onClick={() => onAssign(item)}>🏷️</button>}
                        {onManageRelations && (
                          <button
                            className="adm-btn adm-btn--ghost adm-btn--xs"
                            onClick={() => onManageRelations(item)}
                            title={manageRelationsTitle}
                          >
                            {manageRelationsLabel}
                          </button>
                        )}
                        <DangerButton label="🗑️" onConfirm={() => onDelete(item.id)} />
                      </div>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan={5} className="adm-table__empty">{t('adm_no_entries')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile card list */}
      {!loading && (
        <div className="adm-card-list">
          {items.map(item => {
            const localName = getLocalName(item, currentLanguage);
            const localDesc = getLocalDesc(item, currentLanguage);
            return (
              <div key={item.id} className="adm-card">
                <div className="adm-card__header">
                  {item.url_thumbnail
                    ? <img src={item.url_thumbnail} alt="" className="adm-thumb" />
                    : <div className="adm-thumb adm-thumb--placeholder" />
                  }
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span className="adm-card__title">{localName}</span>
                      <ContentTypeBadge item={item} />
                    </div>
                    {item.name_en && item.name_en !== localName && <div className="adm-card__sub">{item.name_en}</div>}
                    {localDesc && <div className="adm-card__sub">{truncate(localDesc)}</div>}
                  </div>
                  <StatusBadge status={item.status} onToggle={onToggle ? () => onToggle(item) : undefined} />
                </div>
                {item.group_ids?.length > 0 && (
                  <div className="adm-card__rows">
                    <div className="adm-card__row">
                      <span className="adm-card__label">{t('adm_groups')}</span>
                      <div className="adm-chips">{item.group_ids.map(gid => <span key={gid} className="adm-chip">{groupName(gid)}</span>)}</div>
                    </div>
                  </div>
                )}
                <div className="adm-card__actions">
                  <button className="adm-btn adm-btn--ghost adm-btn--xs" onClick={() => onEdit(item)}>✏️ {t('adm_edit')}</button>
                  {canAssignGroups && <button className="adm-btn adm-btn--ghost adm-btn--xs" onClick={() => onAssign(item)}>🏷️ {t('adm_groups')}</button>}
                  {onManageRelations && (
                    <button
                      className="adm-btn adm-btn--ghost adm-btn--xs"
                      onClick={() => onManageRelations(item)}
                      title={manageRelationsTitle}
                    >
                      {manageRelationsLabel} {manageRelationsTitle}
                    </button>
                  )}
                  <DangerButton label="🗑️" onConfirm={() => onDelete(item.id)} />
                </div>
              </div>
            );
          })}
          {items.length === 0 && <p className="adm-table__empty">{t('adm_no_entries')}</p>}
        </div>
      )}
      {children}
    </div>
  );
};

const ContentFormModal = ({ item, isCollection, onClose, onSave }) => {
  const { t, currentLanguage } = useLanguage();
  const [saving, setSaving] = useState(false);

  const langCode = currentLanguage || 'de';
  const nameField = langCode === 'de' ? 'name' : `name_${langCode}`;
  const descField = langCode === 'de' ? 'description' : `description_${langCode}`;

  const langEntry = Object.entries(LANG_MAP).find(([, v]) => v.code === langCode);
  const langLabel = langEntry ? LANG_MAP[langEntry[0]].label : 'DE';

  const [form, setForm] = useState({
    id: item.id,
    name: item[nameField] || item.name || '',
    description: item[descField] || item.description || '',
    name_en: item.name_en || '',
    description_en: item.description_en || '',
    status: item.status ?? 1,
    ...(isCollection ? { is_mandatory: item.is_mandatory ?? 0 } : {}),
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Modal title={`${isCollection ? t('adm_topic_sg') : t('adm_video_sg')} ${t('adm_edit')}`} onClose={onClose}>
      <Field label={`${t('adm_name')} (${langLabel})`}>
        <input className="adm-input" value={form.name} onChange={e => set('name', e.target.value)} />
      </Field>
      <Field label={`${t('adm_name')} (English)`} hint={t('adm_en_readonly_hint')}>
        <input className="adm-input adm-input--readonly" value={form.name_en} readOnly tabIndex={-1} />
      </Field>
      <Field label={`${t('adm_description')} (${langLabel})`}>
        <textarea className="adm-input adm-textarea" rows={3} value={form.description} onChange={e => set('description', e.target.value)} />
      </Field>
      <div className="adm-form-row">
        <Field label={t('adm_status')}>
          <select className="adm-input" value={form.status} onChange={e => set('status', Number(e.target.value))}>
            <option value={1}>{t('adm_active')}</option>
            <option value={0}>{t('adm_inactive')}</option>
          </select>
        </Field>
        {isCollection && (
          <Field label={t('adm_mandatory')}>
            <select className="adm-input" value={form.is_mandatory} onChange={e => set('is_mandatory', Number(e.target.value))}>
              <option value={0}>{t('adm_no')}</option>
              <option value={1}>{t('adm_yes')}</option>
            </select>
          </Field>
        )}
      </div>
      <p className="adm-field__hint">{t('adm_auto_translate_hint')}</p>
      <div className="adm-modal__footer">
        <button className="adm-btn adm-btn--ghost" onClick={onClose} disabled={saving}>{t('adm_cancel')}</button>
        <button className="adm-btn adm-btn--primary" disabled={saving} onClick={async () => {
          setSaving(true);
          await onSave({ ...form, source_lang: langCode });
          setSaving(false);
        }}>
          {saving ? t('adm_translating') : t('adm_save_translate')}
        </button>
      </div>
    </Modal>
  );
};

const SingleContentGroupModal = ({ item, itemType, allGroups, onClose, toast }) => {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);

  const initialGroupIds = allGroups
    .filter(g => (g.content || []).some(
      c => Number(c.element_id) === item.id && Number(c.element_type) === Number(itemType)
    ))
    .map(g => g.id);

  const [current, setCurrent] = useState(initialGroupIds);

  useEffect(() => {
    api(`/admin/content/groups?element_id=${item.id}&element_type=${itemType}`)
      .then(res => { if (Array.isArray(res.group_ids)) setCurrent(res.group_ids); })
      .catch(() => {});
  }, [item.id, itemType]);

  const toggle = async (groupId) => {
    setBusy(true);
    const isAssigned = current.includes(groupId);
    const endpoint = isAssigned ? '/admin/content/remove-group' : '/admin/content/assign-group';
    try {
      await api(endpoint, { method: 'POST', body: JSON.stringify({
        element_id: item.id,
        group_id: groupId,
        element_type: itemType,
      })});
      setCurrent(prev => isAssigned ? prev.filter(id => id !== groupId) : [...prev, groupId]);
      toast(isAssigned ? t('adm_toast_group_removed') : t('adm_toast_group_assigned'), 'success');
    } catch (e) { toast(t('adm_toast_error'), 'error'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`${t('adm_groups_for')}: ${item.name || item.name_en || `ID ${item.id}`}`} onClose={onClose}>
      <p className="adm-modal__sub">{t('adm_content_visible_hint')}</p>
      <div className="adm-group-toggle-list">
        {allGroups.map(g => {
          const active = current.includes(g.id);
          return (
            <button
              key={g.id}
              className={`adm-group-toggle ${active ? 'adm-group-toggle--active' : ''}`}
              onClick={() => toggle(g.id)}
              disabled={busy}
            >
              <span className="adm-group-toggle__name">{g.name}</span>
              <span className="adm-group-toggle__state">{active ? '✓ Zugewiesen' : '+ Hinzufügen'}</span>
            </button>
          );
        })}
        {allGroups.length === 0 && <p className="adm-cell--muted">{t('adm_no_groups')}</p>}
      </div>
      <div className="adm-modal__footer">
        <button className="adm-btn adm-btn--primary" onClick={onClose}>{t('adm_done')}</button>
      </div>
    </Modal>
  );
};

// ─── COLLECTION → ELEMENTS MODAL ─────────────────────────────────────────────
const CollectionElementsModal = ({ collection, toast, onClose }) => {
  const { t, currentLanguage } = useLanguage();
  const [assigned, setAssigned]     = useState([]);
  const [allElems, setAllElems]     = useState([]);
  const [loadingA, setLoadingA]     = useState(true);
  const [loadingE, setLoadingE]     = useState(true);
  const [tab, setTab]               = useState('assigned');
  const [search, setSearch]         = useState('');
  const [busyId, setBusyId]         = useState(null);
  const [orderDirty, setOrderDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  const dragIndexRef  = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const loadAssigned = async () => {
    setLoadingA(true);
    try {
      const res = await api(`/admin/collections/elements?collection_id=${collection.id}`);
      setAssigned(res.data || []);
      setOrderDirty(false);
    } catch { toast('Ladefehler', 'error'); }
    finally { setLoadingA(false); }
  };

  const loadAll = async () => {
    setLoadingE(true);
    try {
      const res = await api('/admin/elements');
      setAllElems(res.data || []);
    } catch {}
    finally { setLoadingE(false); }
  };

  useEffect(() => { loadAssigned(); loadAll(); }, [collection.id]);

  const assignedIds = assigned.map(e => e.id);

  const handleDragStart = (e, index) => {
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => e.target.classList.add('adm-dragging'), 0);
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove('adm-dragging');
    setDragOver(null);
    dragIndexRef.current = null;
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOver !== index) setDragOver(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = dragIndexRef.current;
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragOver(null);
      return;
    }
    setAssigned(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(dropIndex, 0, moved);
      return next;
    });
    setOrderDirty(true);
    setDragOver(null);
    dragIndexRef.current = null;
  };

  const handleSaveOrder = async () => {
    setSavingOrder(true);
    try {
      await api('/admin/collections/reorder-elements', {
        method: 'POST',
        body: JSON.stringify({
          collection_id: collection.id,
          order: assigned.map(e => e.id),
        }),
      });
      toast('✓ Reihenfolge gespeichert', 'success');
      setOrderDirty(false);
    } catch { toast('Fehler beim Speichern der Reihenfolge', 'error'); }
    finally { setSavingOrder(false); }
  };

  const handleToggleStatus = async (elementId, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    setAssigned(prev => prev.map(e => e.id === elementId ? { ...e, status: newStatus } : e));
    try {
      await api('/admin/elements/update', {
        method: 'POST',
        body: JSON.stringify({ id: elementId, status: newStatus }),
      });
      toast(newStatus === 1 ? '✓ Video aktiviert' : 'Video deaktiviert', 'success');
    } catch {
      setAssigned(prev => prev.map(e => e.id === elementId ? { ...e, status: currentStatus } : e));
      toast('Fehler beim Statuswechsel', 'error');
    }
  };

  const handleRemove = async (elementId) => {
    setBusyId(elementId);
    try {
      await api('/admin/collections/remove-element', {
        method: 'POST',
        body: JSON.stringify({ element_id: elementId, collection_id: collection.id }),
      });
      toast('Video entfernt', 'success');
      await loadAssigned();
    } catch { toast('Fehler beim Entfernen', 'error'); }
    finally { setBusyId(null); }
  };

  const handleAdd = async (elementId) => {
    setBusyId(elementId);
    try {
      const res = await api('/admin/collections/assign-element', {
        method: 'POST',
        body: JSON.stringify({ element_id: elementId, collection_id: collection.id }),
      });
      if (res?.status === 'already_exists') {
        toast('Video ist bereits zugeordnet', 'error');
      } else {
        toast('✓ Video hinzugefügt – Nutzer werden benachrichtigt', 'success');
        await loadAssigned();
      }
    } catch (e) { toast(e.message || 'Fehler', 'error'); }
    finally { setBusyId(null); }
  };

  const collName = getLocalName(collection, currentLanguage);

  const addCandidates = allElems.filter(e => {
    const name = getLocalName(e, currentLanguage);
    return !search
      || name.toLowerCase().includes(search.toLowerCase())
      || (e.name_en || '').toLowerCase().includes(search.toLowerCase());
  });

  return (
    <Modal title={`🎬 Videos in: ${collName}`} onClose={onClose} wide>
      <div className="adm-subtabs">
        <button
          className={`adm-subtab ${tab === 'assigned' ? 'adm-subtab--active' : ''}`}
          onClick={() => { setTab('assigned'); setSearch(''); }}
        >
          ✓ Zugeordnete Videos ({assigned.length})
        </button>
        <button
          className={`adm-subtab ${tab === 'add' ? 'adm-subtab--active' : ''}`}
          onClick={() => { setTab('add'); setSearch(''); }}
        >
          + Video hinzufügen
        </button>
      </div>

      {tab === 'assigned' && (
        loadingA ? (
          <div className="adm-loading"><span className="adm-spinner" /></div>
        ) : (
          <>
            {assigned.length > 1 && (
              <p style={{ fontSize: 12, color: '#9CA3AF', margin: '0 0 8px', fontFamily: 'var(--adm-font)' }}>
                ☰ Videos per Drag & Drop in die gewünschte Reihenfolge ziehen.
              </p>
            )}
            <div className="adm-content-toggle-list">
              {assigned.length === 0 && (
                <p className="adm-table__empty">Keine Videos zugeordnet. Klicke auf „+ Video hinzufügen".</p>
              )}
              {assigned.map((e, idx) => {
                const name   = getLocalName(e, currentLanguage);
                const isBusy = busyId === e.id;
                const isDragTarget = dragOver === idx;
                return (
                  <div
                    key={e.id}
                    className={`adm-content-row adm-content-row--active${isDragTarget ? ' adm-content-row--drag-target' : ''}`}
                    draggable
                    onDragStart={ev => handleDragStart(ev, idx)}
                    onDragEnd={handleDragEnd}
                    onDragOver={ev => handleDragOver(ev, idx)}
                    onDrop={ev => handleDrop(ev, idx)}
                    style={{ cursor: 'grab' }}
                  >
                    <span style={{ color: '#D1D5DB', fontSize: 16, cursor: 'grab', flexShrink: 0, userSelect: 'none', paddingRight: 4 }} title="Ziehen zum Sortieren">⠿</span>
                    <span className="adm-cell--muted" style={{ minWidth: 20, fontSize: 12, flexShrink: 0 }}>{idx + 1}</span>
                    {e.url_thumbnail
                      ? <img src={e.url_thumbnail} alt="" className="adm-content-thumb" />
                      : <div className="adm-content-thumb adm-thumb--placeholder" />
                    }
                    <div className="adm-content-info">
                      <span className="adm-content-name">{name}</span>
                      {e.name_en && e.name_en !== name && (
                        <span className="adm-content-sub">{e.name_en}</span>
                      )}
                      {e.duration > 0 && (
                        <span className="adm-content-sub">{Math.round(e.duration / 60)} min</span>
                      )}
                    </div>
                    <StatusBadge
                      status={e.status}
                      onToggle={() => handleToggleStatus(e.id, e.status)}
                    />
                    <DangerButton
                      label={isBusy ? '…' : '– Entfernen'}
                      onConfirm={() => handleRemove(e.id)}
                      disabled={isBusy || busyId !== null}
                    />
                  </div>
                );
              })}
            </div>
          </>
        )
      )}

      {tab === 'add' && (
        <>
          <div className="adm-tab-toolbar adm-tab-toolbar--compact">
            <SearchBar value={search} onChange={setSearch} placeholder="Video suchen…" />
          </div>
          <div style={{ margin: '0 0 10px', padding: '8px 12px', background: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: 8, fontSize: 12, color: '#C2410C', fontFamily: 'var(--adm-font)' }}>
            🔔 Beim Hinzufügen eines Videos erhalten alle Nutzer, die diesen Inhalt bereits angesehen haben, automatisch eine Benachrichtigung.
          </div>
          {loadingE ? (
            <div className="adm-loading"><span className="adm-spinner" /></div>
          ) : (
            <div className="adm-content-toggle-list">
              {addCandidates.map(e => {
                const name       = getLocalName(e, currentLanguage);
                const isAssigned = assignedIds.includes(e.id);
                const isBusy     = busyId === e.id;
                return (
                  <div key={e.id} className={`adm-content-row ${isAssigned ? 'adm-content-row--active' : ''}`}>
                    {e.url_thumbnail
                      ? <img src={e.url_thumbnail} alt="" className="adm-content-thumb" />
                      : <div className="adm-content-thumb adm-thumb--placeholder" />
                    }
                    <div className="adm-content-info">
                      <span className="adm-content-name">{name}</span>
                      {e.name_en && e.name_en !== name && (
                        <span className="adm-content-sub">{e.name_en}</span>
                      )}
                    </div>
                    <StatusBadge status={e.status} />
                    <button
                      className={`adm-btn adm-btn--xs ${isAssigned ? 'adm-btn--ghost' : 'adm-btn--primary'}`}
                      onClick={() => !isAssigned && handleAdd(e.id)}
                      disabled={isBusy || busyId !== null || isAssigned}
                    >
                      {isBusy ? '…' : isAssigned ? '✓ Bereits zugeordnet' : '+ Hinzufügen'}
                    </button>
                  </div>
                );
              })}
              {addCandidates.length === 0 && (
                <p className="adm-table__empty">Keine Videos gefunden.</p>
              )}
            </div>
          )}
        </>
      )}

      <div className="adm-modal__footer">
        {tab === 'assigned' && orderDirty && (
          <button
            className="adm-btn adm-btn--primary"
            onClick={handleSaveOrder}
            disabled={savingOrder}
            style={{ marginRight: 'auto' }}
          >
            {savingOrder ? '⏳ Speichern…' : '💾 Reihenfolge speichern'}
          </button>
        )}
        <button className="adm-btn adm-btn--ghost" onClick={onClose}>Fertig</button>
      </div>
    </Modal>
  );
};

// ─── ELEMENT → COLLECTIONS MODAL ─────────────────────────────────────────────
const ElementCollectionsModal = ({ element, toast, onClose }) => {
  const { t, currentLanguage } = useLanguage();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [busyId, setBusyId]           = useState(null);

  const elemName = getLocalName(element, currentLanguage);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api(`/admin/elements/collections?element_id=${element.id}`);
      setCollections(res.data || []);
    } catch { toast('Ladefehler', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [element.id]);

  const handleRemove = async (collectionId) => {
    setBusyId(collectionId);
    try {
      await api('/admin/collections/remove-element', {
        method: 'POST',
        body: JSON.stringify({ element_id: element.id, collection_id: collectionId }),
      });
      toast('Zuordnung entfernt', 'success');
      setCollections(prev => prev.filter(c => c.id !== collectionId));
    } catch { toast('Fehler beim Entfernen', 'error'); }
    finally { setBusyId(null); }
  };

  return (
    <Modal title={`📚 Inhalte mit Video: ${elemName}`} onClose={onClose} wide>
      <p className="adm-modal__sub">
        Dieses Video ist in {collections.length} Inhalt{collections.length !== 1 ? 'en' : ''} enthalten.
        Die Zuordnung zu einzelnen Inhalten kann hier entfernt werden.
      </p>

      {loading ? (
        <div className="adm-loading"><span className="adm-spinner" /></div>
      ) : (
        <div className="adm-content-toggle-list">
          {collections.length === 0 && (
            <p className="adm-table__empty">Dieses Video ist keinem Inhalt zugeordnet.</p>
          )}
          {collections.map(c => {
            const name   = getLocalName(c, currentLanguage);
            const isBusy = busyId === c.id;
            return (
              <div key={c.id} className="adm-content-row adm-content-row--active">
                {c.url_thumbnail
                  ? <img src={c.url_thumbnail} alt="" className="adm-content-thumb" />
                  : <div className="adm-content-thumb adm-thumb--placeholder" />
                }
                <div className="adm-content-info">
                  <span className="adm-content-name">{name}</span>
                  {c.name_en && c.name_en !== name && (
                    <span className="adm-content-sub">{c.name_en}</span>
                  )}
                </div>
                <StatusBadge status={c.status} />
                <DangerButton
                  label={isBusy ? '…' : '– Zuordnung entfernen'}
                  onConfirm={() => handleRemove(c.id)}
                  disabled={isBusy || busyId !== null}
                />
              </div>
            );
          })}
        </div>
      )}

      <div className="adm-modal__footer">
        <button className="adm-btn adm-btn--primary" onClick={onClose}>Fertig</button>
      </div>
    </Modal>
  );
};

// ─── NOTIFICATIONS TAB ───────────────────────────────────────────────────────
// DEPRECATED: Ersetzt durch SendMessagePanel aus AdminPanel_SendMessage.jsx (v2).
// Diese Komponente bleibt als Dead-Code bis zum nächsten Cleanup-Zyklus.
// Render-Stelle in AdminPanel: activeTab === 'notifications' → <SendMessagePanel />
//
// LANG_OPTIONS_NOTIF und TARGET_LABELS bleiben als Modul-Konstanten erhalten,
// da QuickMessageModal und NotificationArchiveTab sie weiterhin nutzen.
const LANG_OPTIONS_NOTIF = [
  { code: 'de', label: 'Deutsch' },  { code: 'en', label: 'English' },
  { code: 'ru', label: 'Русский' },  { code: 'ar', label: 'عربي' },
  { code: 'fr', label: 'Français' }, { code: 'es', label: 'Español' },
  { code: 'pl', label: 'Polski' },   { code: 'ro', label: 'Română' },
  { code: 'tr', label: 'Türkçe' },   { code: 'hr', label: 'Hrvatski' },
];
const TARGET_LABELS = { all: '🌍 Alle', group: '👥 Gruppe', user: '👤 Nutzer' };

// eslint-disable-next-line no-unused-vars
const _DEPRECATED_NotificationsTab = ({ toast, allUsers = [], allGroups = [] }) => null;

// ─── NOTIFICATION ARCHIVE TAB ────────────────────────────────────────────────

const NotificationArchiveTab = ({ toast }) => {
  const [archive,        setArchive]        = React.useState([]);
  const [archiveLoading, setArchiveLoading] = React.useState(false);
  const [archiveSearch,  setArchiveSearch]  = React.useState('');
  const [selectedMsg,    setSelectedMsg]    = React.useState(null);
  const [deletingId,     setDeletingId]     = React.useState(null);

  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
         + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const loadArchive = React.useCallback(async (q = '') => {
    setArchiveLoading(true);
    try {
      const qs  = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
      const res = await api(`/admin/notifications${qs}`);
      if (res?.status === 'ok') setArchive(res.data || []);
    } catch (e) {
      toast('⚠ Archiv konnte nicht geladen werden', 'error');
    } finally { setArchiveLoading(false); }
  }, [toast]);

  React.useEffect(() => { loadArchive(''); }, [loadArchive]);

  const searchTimer = React.useRef(null);
  const handleSearchChange = (val) => {
    setArchiveSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => loadArchive(val), 350);
  };

  const handleDelete = async (id, fromModal = false) => {
    if (!window.confirm('Nachricht wirklich löschen? Sie wird bei allen Nutzern aus dem Posteingang entfernt.')) return;
    setDeletingId(id);
    try {
      const res = await api(`/admin/notifications/${id}`, { method: 'DELETE' });
      if (res?.status === 'ok') {
        setArchive(prev => prev.filter(m => m.id !== id));
        if (fromModal) setSelectedMsg(null);
        toast('✓ Nachricht gelöscht', 'success');
      } else throw new Error(res?.message || 'Fehler');
    } catch (e) {
      toast('⚠ ' + (e.message || 'Löschen fehlgeschlagen'), 'error');
    } finally { setDeletingId(null); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ position: 'relative' }}>
        <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
          fontSize: 14, color: '#9CA3AF', pointerEvents: 'none' }}>🔍</span>
        <input className="adm-input" placeholder="Nachrichten durchsuchen…"
          style={{ paddingLeft: 36 }}
          value={archiveSearch}
          onChange={e => handleSearchChange(e.target.value)} />
      </div>

      {!archiveLoading && archive.length > 0 && (
        <p style={{ margin: 0, fontSize: 12, color: '#9CA3AF' }}>
          {archive.length} Nachricht{archive.length !== 1 ? 'en' : ''} gefunden
        </p>
      )}

      {archiveLoading ? (
        <p style={{ fontSize: 13, color: '#9CA3AF', textAlign: 'center', padding: '32px 0', margin: 0 }}>Lade…</p>
      ) : archive.length === 0 ? (
        <p style={{ fontSize: 13, color: '#9CA3AF', textAlign: 'center', padding: '32px 0', margin: 0 }}>
          {archiveSearch ? 'Keine Ergebnisse.' : 'Noch keine Nachrichten gesendet.'}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0,
          border: '1.5px solid #E5E7EB', borderRadius: 14, overflow: 'hidden' }}>
          {archive.map((msg, idx) => (
            <div key={msg.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '13px 14px',
                background: '#fff', borderTop: idx > 0 ? '1px solid #F3F4F6' : 'none',
                cursor: 'pointer',
              }}
              onClick={() => setSelectedMsg(msg)}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#111827',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {msg.title}
                </p>
                <p style={{ margin: '3px 0 0', fontSize: 11, color: '#9CA3AF' }}>
                  {TARGET_LABELS[msg.target_type] || msg.target_type}
                  {' · '}{fmtDate(msg.created_at)}
                  {' · '}👁 {msg.read_count ?? 0} gelesen
                </p>
              </div>
              <button type="button"
                className="adm-btn adm-btn--danger-ghost adm-btn--xs"
                style={{ flexShrink: 0, opacity: deletingId === msg.id ? 0.4 : 1 }}
                disabled={deletingId === msg.id}
                onClick={e => { e.stopPropagation(); handleDelete(msg.id); }}
              >
                {deletingId === msg.id ? '…' : '🗑'}
              </button>
            </div>
          ))}
        </div>
      )}

      {selectedMsg && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}
          onClick={() => setSelectedMsg(null)}
        >
          <div style={{
            background: '#fff', borderRadius: 18, padding: 24, width: '100%', maxWidth: 420,
            boxShadow: '0 20px 60px rgba(0,0,0,0.20)', display: 'flex', flexDirection: 'column', gap: 16,
          }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: '#9CA3AF',
                  textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  {TARGET_LABELS[selectedMsg.target_type] || selectedMsg.target_type}
                  {' · '}{fmtDate(selectedMsg.created_at)}
                  {' · '}👁 {selectedMsg.read_count ?? 0} gelesen
                </p>
                <p style={{ margin: '4px 0 0', fontSize: 16, fontWeight: 800, color: '#111827', lineHeight: 1.3 }}>
                  {selectedMsg.title}
                </p>
              </div>
              <button type="button" className="adm-btn adm-btn--ghost adm-btn--xs"
                onClick={() => setSelectedMsg(null)} style={{ flexShrink: 0 }}>✕</button>
            </div>
            <div style={{ background: '#F9FAFB', border: '1.5px solid #E5E7EB', borderRadius: 12, padding: '12px 14px' }}>
              <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {selectedMsg.body}
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 4, borderTop: '1px solid #F3F4F6' }}>
              <button type="button" className="adm-btn adm-btn--ghost"
                onClick={() => setSelectedMsg(null)}>Schließen</button>
              <button type="button" className="adm-btn adm-btn--danger"
                disabled={deletingId === selectedMsg.id}
                onClick={() => handleDelete(selectedMsg.id, true)}>
                {deletingId === selectedMsg.id ? '…' : '🗑 Löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ─── DOCUMENTS TAB ───────────────────────────────────────────────────────────

const ENRICH_STATUS = {
  enriched:      { label: 'Cog-RAG ✓',    color: '#15803D', bg: '#F0FDF4', border: '#BBF7D0' },
  done:          { label: 'RAG bereit',    color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE' },
  processing:    { label: 'Läuft…',        color: '#9CA3AF', bg: '#F9FAFB', border: '#E5E7EB' },
  enrich_failed: { label: 'Fehler',        color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
  failed:        { label: 'Fehlgeschlagen',color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
  pending:       { label: 'Ausstehend',    color: '#9CA3AF', bg: '#F9FAFB', border: '#E5E7EB' },
};

const EnrichBadge = ({ status }) => {
  const s = ENRICH_STATUS[status] || ENRICH_STATUS.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 9px', borderRadius: 10, fontSize: 11, fontWeight: 600,
      color: s.color, background: s.bg, border: `1px solid ${s.border}`,
      whiteSpace: 'nowrap', fontFamily: 'Inter, sans-serif',
    }}>
      {s.label}
    </span>
  );
};

const EnrichProgress = ({ pct, total, enriched }) => {
  const color = pct >= 100 ? '#15803D' : pct > 0 ? '#FF6407' : '#E5E7EB';
  return (
    <div style={{ minWidth: 120 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#6B7280', marginBottom: 3 }}>
        <span>{enriched}/{total} Chunks</span>
        <span style={{ fontWeight: 700, color }}>{pct}%</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: '#F3F4F6', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.4s' }} />
      </div>
    </div>
  );
};

// ─── VIDEOS RAG TAB ───────────────────────────────────────────────────────────

const VideosRagTab = ({ toast }) => {
  const { t } = useLanguage();
  const [videos, setVideos]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [reEnriching, setReEnriching] = useState(new Set());
  const pollRef = useRef(null);
  const [polling, setPolling] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api('/admin/videos/status');
      setVideos(res.data || []);
    } catch (e) {
      if (!silent) toast('Fehler beim Laden der Videos', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const active = videos.some(v => v.embedding_status === 'processing');
    if (active && !pollRef.current) {
      setPolling(true);
      pollRef.current = setInterval(() => load(true), 5000);
    } else if (!active && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null; setPolling(false);
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [videos, load]);

  const handleReEnrich = async (v) => {
    setReEnriching(prev => new Set([...prev, v.element_id]));
    try {
      await api('/admin/videos/re-enrich', { method: 'POST', body: JSON.stringify({ element_id: v.element_id }) });
      toast('Enrichment gestartet', 'success');
      setTimeout(() => load(true), 1500);
    } catch (e) { toast('Fehler', 'error'); }
    finally { setReEnriching(prev => { const s = new Set(prev); s.delete(v.element_id); return s; }); }
  };

  const filtered = videos
    .filter(v => (v.element_name || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => b.element_id - a.element_id);
  const pendingCount  = videos.filter(v => ['done','partial'].includes(v.embedding_status)).length;
  const enrichedCount = videos.filter(v => v.embedding_status === 'enriched').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', padding: '8px 12px', background: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB' }}>
        {[
          { label: 'Gesamt',     val: videos.length,  color: '#374151', bg: '#F3F4F6', border: '#E5E7EB' },
          { label: 'Cog-RAG',   val: enrichedCount,  color: '#15803D', bg: '#F0FDF4', border: '#BBF7D0' },
          { label: 'Offen',     val: pendingCount,   color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE' },
        ].map(c => (
          <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 6, background: c.bg, border: '1px solid ' + c.border }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: c.color }}>{c.val}</span>
            <span style={{ fontSize: 11, color: '#6B7280' }}>{c.label}</span>
          </div>
        ))}
        {polling && <span style={{ fontSize: 11, color: '#FF6407' }}>Live</span>}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input className="adm-search" style={{ flex: 1, minWidth: 140 }} placeholder="Video suchen..." value={search} onChange={e => setSearch(e.target.value)} />
        <button className="adm-btn adm-btn--secondary" style={{ padding: '5px 10px', fontSize: 12 }} onClick={() => load(false)}>Neu laden</button>
        {pendingCount > 0 && (
          <button className="adm-btn adm-btn--primary" style={{ background: '#FF6407', border: 'none', color: '#fff', padding: '5px 12px', fontSize: 12 }}
            onClick={async () => {
              try {
                const res = await api('/admin/videos/re-enrich-all', { method: 'POST', body: '{}' });
                toast((res.started || 0) + ' Jobs gestartet', 'success');
                setTimeout(() => load(true), 1500);
              } catch (e) { toast('Fehler', 'error'); }
            }}>Alle {pendingCount} anreichern</button>
        )}
      </div>
      <div className="adm-card-list" style={{ display: 'flex' }}>
        {loading && <div className="adm-card" style={{ textAlign: 'center', padding: 24, color: 'var(--adm-text-muted)' }}>Lade...</div>}
        {!loading && videos.length === 0 && <div className="adm-card" style={{ textAlign: 'center', padding: 24, color: 'var(--adm-text-muted)' }}>Keine Videos mit Transkript-Chunks gefunden</div>}
        {!loading && filtered.map(v => {
          const pct  = v.enrichment_pct || 0;
          const smap = {
            enriched:   { label: 'Cog-RAG aktiv', color: '#15803D', bg: '#F0FDF4', border: '#BBF7D0' },
            partial:    { label: 'Teilweise',     color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
            done:       { label: 'Offen',         color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE' },
            pending:    { label: 'Keine Chunks',  color: '#9CA3AF', bg: '#F9FAFB', border: '#E5E7EB' },
            processing: { label: 'Laeuft...',     color: '#9CA3AF', bg: '#F9FAFB', border: '#E5E7EB' },
          };
          const s = smap[v.embedding_status] || smap.pending;
          return (
            <div key={v.element_id} className="adm-card">
              <div className="adm-card__header">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {v.element_hash ? (
                      <a href={`/element/${v.element_hash}`} target="_blank" rel="noopener noreferrer"
                        title={t('adm_open_element')} onClick={e => e.stopPropagation()}
                        className="adm-card__title"
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#111827', textDecoration: 'none', fontWeight: 600 }}
                        onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                      >{v.element_name || '-'}</a>
                    ) : (
                      <div className="adm-card__title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.element_name || '-'}</div>
                    )}
                    {v.element_hash ? (
                      <a href={`/element/${v.element_hash}`} target="_blank" rel="noopener noreferrer"
                        title={t('adm_open_video')} onClick={e => e.stopPropagation()}
                        style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4, color: '#6D28D9', background: '#F5F3FF', border: '1px solid #DDD6FE', letterSpacing: '0.02em', whiteSpace: 'nowrap', textDecoration: 'none', cursor: 'pointer', transition: 'filter 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.filter = 'brightness(0.88)'}
                        onMouseLeave={e => e.currentTarget.style.filter = ''}
                      >Video ↗</a>
                    ) : (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4, color: '#6D28D9', background: '#F5F3FF', border: '1px solid #DDD6FE', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>Video</span>
                    )}
                  </div>
                  <div className="adm-card__sub">{'eid=' + v.element_id + (v.element_hash ? ' · ' + v.element_hash : '')}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 8, color: s.color, background: s.bg, border: '1px solid ' + s.border, whiteSpace: 'nowrap' }}>{s.label}</span>
              </div>
              <div className="adm-card__rows">
                <div className="adm-card__row">
                  <span className="adm-card__label">Cog-RAG</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 80, height: 5, background: '#E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: pct + '%', background: pct >= 100 ? '#15803D' : '#FF6407', borderRadius: 3 }} />
                    </div>
                    <span style={{ fontSize: 11, color: '#6B7280' }}>{(v.chunks_enriched || 0) + '/' + (v.chunks_total || 0) + ' (' + pct + '%)'}</span>
                  </div>
                </div>
                <div className="adm-card__row">
                  <span className="adm-card__label">Aktualisiert</span>
                  <span style={{ fontSize: 12, color: '#6B7280' }}>{v.last_update ? new Date(v.last_update).toLocaleDateString('de-DE') : '-'}</span>
                </div>
              </div>
              <div className="adm-card__actions">
                <button className="adm-btn adm-btn--ghost adm-btn--xs"
                  disabled={reEnriching.has(v.element_id) || v.embedding_status === 'processing'}
                  onClick={() => handleReEnrich(v)}>
                  {reEnriching.has(v.element_id) ? 'Laeuft...' : v.embedding_status === 'enriched' ? 'Re-Enrich' : 'Cog-RAG starten'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── RAG WRAPPER TAB (Dokumente + Videos) ────────────────────────────────────

const RagTab = ({ toast }) => {
  const [sub, setSub] = useState('documents');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #E5E7EB', paddingBottom: 8 }}>
        <button className={'adm-subtab' + (sub === 'documents' ? ' adm-subtab--active' : '')} onClick={() => setSub('documents')}>
          Dokumente
        </button>
        <button className={'adm-subtab' + (sub === 'videos' ? ' adm-subtab--active' : '')} onClick={() => setSub('videos')}>
          Videos
        </button>
      </div>
      {sub === 'documents' && <DocumentsTab toast={toast} />}
      {sub === 'videos'    && <VideosRagTab toast={toast} />}
    </div>
  );
};

const DocumentsTab = ({ toast }) => {
  const { t } = useLanguage();
  const [docs, setDocs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState('all');
  const [polling, setPolling]   = useState(false);
  const [reEnriching, setReEnriching] = useState(new Set());
  const pollRef                 = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api('/admin/documents/status');
      setDocs(res.data || []);
    } catch (e) {
      if (!silent) toast('Fehler beim Laden der Dokumente', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const isActivelyProcessing = docs.some(d => d.embedding_status === 'processing');
    if (isActivelyProcessing && !pollRef.current) {
      setPolling(true);
      pollRef.current = setInterval(() => load(true), 4000);
    } else if (!isActivelyProcessing && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
      setPolling(false);
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [docs, load]);

  const handleReEnrich = async (doc) => {
    setReEnriching(prev => new Set([...prev, doc.id]));
    try {
      await api('/admin/documents/re-enrich', {
        method: 'POST',
        body: JSON.stringify({ file_storage_id: doc.id }),
      });
      toast(`Enrichment für „${doc.original_name}" gestartet`, 'success');
      setTimeout(() => load(true), 1500);
    } catch (e) {
      toast('Enrichment konnte nicht gestartet werden', 'error');
    } finally {
      setReEnriching(prev => { const s = new Set(prev); s.delete(doc.id); return s; });
    }
  };

  const filtered = docs
    .filter(d => {
      const matchSearch = d.original_name.toLowerCase().includes(search.toLowerCase())
        || (d.element_name || '').toLowerCase().includes(search.toLowerCase());
      const matchFilter = filter === 'all' ? true
        : filter === 'enriched'      ? d.embedding_status === 'enriched'
        : filter === 'pending'       ? ['done', 'processing'].includes(d.embedding_status)
        : filter === 'failed'        ? ['failed', 'enrich_failed'].includes(d.embedding_status)
        : true;
      return matchSearch && matchFilter;
    })
    .sort((a, b) => b.id - a.id);

  const counts = docs.reduce((acc, d) => {
    if (d.embedding_status === 'enriched') acc.enriched++;
    else if (['done', 'processing'].includes(d.embedding_status)) acc.pending++;
    else if (['failed', 'enrich_failed'].includes(d.embedding_status)) acc.failed++;
    return acc;
  }, { enriched: 0, pending: 0, failed: 0 });

  const totalChunks    = docs.reduce((s, d) => s + d.chunks_total, 0);
  const enrichedChunks = docs.reduce((s, d) => s + d.chunks_enriched, 0);
  const overallPct     = totalChunks > 0 ? Math.round(enrichedChunks / totalChunks * 100) : 0;

  const fmt = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <div className="adm-tab-content">
      <div style={{
        display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20,
        padding: '14px 18px', background: '#F9FAFB', borderRadius: 12,
        border: '1px solid #E5E7EB',
      }}>
        {[
          { label: 'Gesamt',       val: docs.length,        color: '#374151', bg: '#F3F4F6',  border: '#E5E7EB'  },
          { label: 'Cog-RAG ✓',   val: counts.enriched,    color: '#15803D', bg: '#F0FDF4',  border: '#BBF7D0'  },
          { label: 'Ausstehend',   val: counts.pending,     color: '#1D4ED8', bg: '#EFF6FF',  border: '#BFDBFE'  },
          { label: 'Fehler',       val: counts.failed,      color: '#DC2626', bg: '#FEF2F2',  border: '#FECACA'  },
        ].map(c => (
          <div key={c.label} style={{
            display: 'flex', flexDirection: 'column', gap: 2,
            padding: '8px 14px', borderRadius: 8, background: c.bg,
            border: `1px solid ${c.border}`, minWidth: 80,
          }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: c.color, lineHeight: 1 }}>{c.val}</span>
            <span style={{ fontSize: 11, color: '#6B7280' }}>{c.label}</span>
          </div>
        ))}
        <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6B7280' }}>
            <span>Cog-RAG Coverage gesamt</span>
            <span style={{ fontWeight: 700, color: overallPct >= 100 ? '#15803D' : '#FF6407' }}>{overallPct}%</span>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: '#E5E7EB', overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 4, width: `${overallPct}%`, background: overallPct >= 100 ? '#15803D' : '#FF6407', transition: 'width 0.5s' }} />
          </div>
          <div style={{ fontSize: 10, color: '#9CA3AF' }}>
            {enrichedChunks.toLocaleString()} / {totalChunks.toLocaleString()} Chunks angereichert
          </div>
        </div>
        {polling && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#FF6407' }}>
            <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
            Live-Update aktiv
          </div>
        )}
      </div>

      <div className="adm-tab-toolbar">
        <input
          className="adm-search"
          placeholder="Dokument oder Element suchen…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <StatusFilter
          value={filter}
          onChange={setFilter}
          options={[
            { val: 'all',      label: 'Alle' },
            { val: 'enriched', label: 'Cog-RAG ✓' },
            { val: 'pending',  label: 'Ausstehend' },
            { val: 'failed',   label: 'Fehler' },
          ]}
        />
        <button className="adm-btn adm-btn--secondary" onClick={() => load(false)}>↻ Aktualisieren</button>
        <button
          className="adm-btn adm-btn--secondary"
          style={{ fontSize: 11 }}
          title="Diagnose: Prüft ob der Hintergrundprozess funktioniert"
          onClick={async () => {
            try {
              const res = await api('/admin/documents/worker-diag');
              const d = res.diag;
              const lines = [
                `exec() verfügbar: ${d.exec_available ? '✓' : '✗ FEHLER'}`,
                `exec in disable_functions: ${d.exec_in_disable_functions ? '✗ JA' : '✓ nein'}`,
                `PHP_BINARY: ${d.PHP_BINARY_const}`,
                `PHP Binary gewählt: ${d.php_binary_selected || '✗ KEINER GEFUNDEN'}`,
                `Worker-Script: ${d.worker_found || '✗ NICHT GEFUNDEN'}`,
                `tmp/ schreibbar: ${d.tmp_writable ? '✓' : '✗'}`,
                `output/ schreibbar: ${d.output_writable ? '✓' : '✗'}`,
                d.background_launch_test ? `Hintergrund-Launch-Test: ${d.background_launch_test.log_created ? '✓ OK' : '✗ FEHLGESCHLAGEN'}` : '',
              ].filter(Boolean).join('\n');
              alert('Worker Diagnose:\n\n' + lines);
            } catch (e) { toast('Diagnose fehlgeschlagen: ' + e.message, 'error'); }
          }}
        >
          🔍 Diagnose
        </button>
        {counts.pending > 0 && (
          <button
            className="adm-btn adm-btn--primary"
            style={{ background: '#FF6407', border: 'none', color: '#fff' }}
            onClick={async () => {
              try {
                const res = await api('/admin/documents/re-enrich-all', { method: 'POST', body: '{}' });
                toast(`${res.started || 0} Enrichment-Jobs gestartet`, 'success');
                setTimeout(() => load(true), 1500);
              } catch (e) { toast('Fehler beim Starten', 'error'); }
            }}
          >
            ▶ Alle {counts.pending} anreichern
          </button>
        )}
      </div>

      <div className="adm-card-list" style={{ display: 'flex' }}>
        {loading && (
          <div className="adm-card" style={{ textAlign: 'center', padding: 24, color: 'var(--adm-text-muted)' }}>Lade...</div>
        )}
        {!loading && docs.length === 0 && (
          <div className="adm-card" style={{ textAlign: 'center', padding: 24, color: 'var(--adm-text-muted)' }}>Keine Dokumente vorhanden</div>
        )}
        {!loading && docs.length > 0 && filtered.length === 0 && (
          <div className="adm-card" style={{ textAlign: 'center', padding: 24, color: 'var(--adm-text-muted)' }}>
            Keine Treffer ({docs.length} Dokumente geladen, Filter aktiv)
          </div>
        )}
        {!loading && filtered.map(doc => {
          const pct  = doc.enrichment_pct || 0;
          const smap = {
            enriched:      { label: 'Cog-RAG aktiv', color: '#15803D', bg: '#F0FDF4', border: '#BBF7D0' },
            done:          { label: 'RAG bereit',     color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE' },
            processed:     { label: 'RAG bereit',     color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE' },
            processing:    { label: 'Laeuft...',      color: '#9CA3AF', bg: '#F9FAFB', border: '#E5E7EB' },
            enrich_failed: { label: 'Fehler',         color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
            failed:        { label: 'Fehler',         color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
          };
          const s   = smap[doc.embedding_status] || { label: doc.embedding_status || '?', color: '#9CA3AF', bg: '#F9FAFB', border: '#E5E7EB' };
          const fmtB = (b) => b >= 1048576 ? ((b/1048576).toFixed(1) + ' MB') : (Math.round(b/1024) + ' KB');
          const bar = pct >= 100 ? '#15803D' : pct > 0 ? '#FF6407' : '#E5E7EB';
          return (
            <div key={doc.id} className="adm-card">
              <div className="adm-card__header">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <div className="adm-card__title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {doc.original_name || '-'}
                    </div>
                    {doc.element_hash ? (
                      <a href={`/element/${doc.element_hash}`} target="_blank" rel="noopener noreferrer"
                        title={t('adm_open_doc')} onClick={e => e.stopPropagation()}
                        style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4, color: '#0369A1', background: '#F0F9FF', border: '1px solid #BAE6FD', letterSpacing: '0.02em', textDecoration: 'none', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'filter 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.filter = 'brightness(0.88)'}
                        onMouseLeave={e => e.currentTarget.style.filter = ''}
                      >Dok. ↗</a>
                    ) : (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4, color: '#0369A1', background: '#F0F9FF', border: '1px solid #BAE6FD', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>Dok.</span>
                    )}
                  </div>
                  <div className="adm-card__sub">
                    {'fid=' + doc.id + (doc.element_name ? ' · ' : '')}
                    {doc.element_name && doc.element_hash ? (
                      <a href={`/element/${doc.element_hash}`} target="_blank" rel="noopener noreferrer"
                        title={t('adm_open_element')} onClick={e => e.stopPropagation()}
                        style={{ color: '#FF6407', textDecoration: 'none', fontWeight: 600 }}
                        onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                      >{doc.element_name} ↗</a>
                    ) : doc.element_name || null}
                  </div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 8,
                  color: s.color, background: s.bg, border: '1px solid ' + s.border, whiteSpace: 'nowrap' }}>
                  {s.label}
                </span>
              </div>
              <div className="adm-card__rows">
                <div className="adm-card__row">
                  <span className="adm-card__label">Cog-RAG</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 80, height: 5, background: '#E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: pct + '%', background: bar, borderRadius: 3 }} />
                    </div>
                    <span style={{ fontSize: 11, color: '#6B7280' }}>
                      {(doc.chunks_enriched || 0) + '/' + (doc.chunks_total || 0) + ' (' + pct + '%)'}
                    </span>
                  </div>
                </div>
                <div className="adm-card__row">
                  <span className="adm-card__label">Groesse</span>
                  <span style={{ fontSize: 12, color: '#6B7280' }}>{fmtB(doc.file_size || 0)}</span>
                </div>
                <div className="adm-card__row">
                  <span className="adm-card__label">Aktualisiert</span>
                  <span style={{ fontSize: 12, color: '#6B7280' }}>
                    {doc.updated_at ? new Date(doc.updated_at).toLocaleDateString('de-DE') : '-'}
                  </span>
                </div>
              </div>
              <div className="adm-card__actions">
                <button
                  className="adm-btn adm-btn--ghost adm-btn--xs"
                  disabled={reEnriching.has(doc.id) || doc.embedding_status === 'processing'}
                  onClick={() => handleReEnrich(doc)}
                >
                  {reEnriching.has(doc.id) ? 'Laeuft...'
                    : doc.embedding_status === 'enriched' ? 'Re-Enrich'
                    : 'Cog-RAG starten'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ─── MAIN AdminPanel ──────────────────────────────────────────────────────────
const ADMIN_GROUPS = [0, 2, 3];

// ─── FragHejVi Tab ────────────────────────────────────────────────────────────

const FHQV_LANGS = ['de','en','fr','es','ru','pl','ar','ro','tr','hr'];

const FragHejViTab = ({ toast }) => {
  const { t, currentLanguage } = useLanguage();
  const lang = FHQV_LANGS.includes(currentLanguage) ? currentLanguage : 'de';

  const [questions,    setQuestions]    = React.useState([]);
  const [loading,      setLoading]      = React.useState(true);
  const [search,       setSearch]       = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('unanswered');
  const [modal,        setModal]        = React.useState(null);
  const [answerText,   setAnswerText]   = React.useState('');
  const [docFile,      setDocFile]      = React.useState(null);
  const [saving,       setSaving]       = React.useState(false);
  const [marking,      setMarking]      = React.useState(false);
  const [suggesting,   setSuggesting]   = React.useState(false);
  const [extracting,   setExtracting]   = React.useState(false);
  const [suggestedTitle, setSuggestedTitle] = React.useState('');
  const [docSummary,   setDocSummary]   = React.useState('');
  const fileRef = React.useRef(null);

  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
         + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const load = React.useCallback(async (q = search, s = statusFilter) => {
    setLoading(true);
    try {
      const qs  = new URLSearchParams({ status: s, ...(q.trim() && { q: q.trim() }) }).toString();
      const res = await api(`/admin/frag-hejvi/questions?${qs}`);
      if (res?.status === 'ok') setQuestions(res.data || []);
    } catch (e) {
      if (e.code !== 'FEATURE_GATED' && e.code !== 'LICENCE_EXPIRED') {
        toast('⚠ Fragen konnten nicht geladen werden', 'error');
      }
    } finally { setLoading(false); }
  }, [toast, search, statusFilter]);

  React.useEffect(() => { load(); }, [load]);

  const searchTimer = React.useRef(null);
  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(val, statusFilter), 350);
  };

  const openModal = (q) => {
    setModal(q);
    setAnswerText(q.answer_text || '');
    setDocFile(null);
    setExtracting(false);
    setSuggestedTitle('');
    setDocSummary('');
  };
  const closeModal = () => {
    setModal(null); setAnswerText(''); setDocFile(null); setExtracting(false);
    setSuggestedTitle(''); setDocSummary('');
  };

  const handleFileSelect = React.useCallback(async (file) => {
    if (!file || !modal) return;
    setDocFile(file);
    setExtracting(true);
    setAnswerText('');
    setSuggestedTitle('');
    setDocSummary('');

    try {
      const fd = new FormData();
      fd.append('question_id', String(modal.id));
      fd.append('document', file);
      const res = await api('/admin/frag-hejvi/extract-from-document', { method: 'POST', body: fd });
      if (res?.status === 'ok') {
        if (res.extracted_answer) setAnswerText(res.extracted_answer);
        if (res.suggested_title)  setSuggestedTitle(res.suggested_title);
        if (res.doc_summary)      setDocSummary(res.doc_summary);
        toast('✨ Antwort aus Dokument extrahiert — bitte prüfen und ggf. anpassen', 'success');
      } else {
        toast('⚠ Extraktion hat keinen Text geliefert — bitte manuell eingeben', 'warning');
      }
    } catch (e) {
      toast('⚠ Extraktion fehlgeschlagen: ' + (e.message || 'Fehler'), 'error');
    } finally {
      setExtracting(false);
    }
  }, [modal, toast]);

  const canSave = answerText.trim().length > 0 && !saving && !extracting;
  const canMark = !marking && modal?.status !== 'answered';

  const handleSave = async () => {
    if (!canSave || !modal) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('question_id', String(modal.id));
      fd.append('answer_text', answerText.trim());
      if (docFile)        fd.append('document',       docFile);
      if (suggestedTitle) fd.append('document_title', suggestedTitle);
      if (docSummary)     fd.append('doc_summary',    docSummary);
      const res = await api('/admin/frag-hejvi/answer', { method: 'POST', body: fd });
      if (res?.status === 'ok') {
        const parts = ['✓ Antwort gespeichert'];
        if (res.element_id) parts.push('Element erstellt');
        if (res.notified)   parts.push('Nutzer benachrichtigt ✉️');
        toast(parts.join(' · '), 'success');
        closeModal();
        load(search, statusFilter);
      } else throw new Error(res?.message || 'Fehler');
    } catch (e) {
      toast('⚠ ' + (e.message || 'Fehler beim Speichern'), 'error');
    } finally { setSaving(false); }
  };

  const handleMarkAnswered = async () => {
    if (!canMark || !modal) return;
    setMarking(true);
    try {
      const res = await api('/admin/frag-hejvi/mark-answered', {
        method: 'POST',
        body: JSON.stringify({ question_id: modal.id }),
      });
      if (res?.status === 'ok') {
        toast('✓ Als beantwortet markiert', 'success');
        closeModal();
        load(search, statusFilter);
      } else throw new Error(res?.message || 'Fehler');
    } catch (e) {
      toast('⚠ ' + (e.message || 'Fehler'), 'error');
    } finally { setMarking(false); }
  };

  const handleSuggest = async () => {
    if (!modal || suggesting) return;
    setSuggesting(true);
    try {
      const res = await api('/admin/frag-hejvi/suggest-answer', {
        method: 'POST',
        body: JSON.stringify({ question_id: modal.id }),
      });
      if (res?.status === 'ok' && res.suggestion) {
        setAnswerText(res.suggestion);
        toast('✨ Antwortvorschlag eingefügt — bitte prüfen und ggf. anpassen', 'success');
      } else {
        throw new Error(res?.message || 'Kein Vorschlag erhalten');
      }
    } catch (e) {
      toast('⚠ ' + (e.message || 'Fehler beim Vorschlag'), 'error');
    } finally { setSuggesting(false); }
  };

  const s = {
    card: { background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 14, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8, transition: 'box-shadow 0.15s' },
    cardHeader: { display: 'flex', alignItems: 'flex-start', gap: 10 },
    questionText: { flex: 1, margin: 0, fontSize: 14, fontWeight: 500, color: '#111827', lineHeight: 1.5 },
    meta: { display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontSize: 11, color: '#9CA3AF' },
    badge: (answered) => ({ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: answered ? '#D1FAE5' : '#FEF3C7', color: answered ? '#065F46' : '#92400E' }),
    btnAnswer: { alignSelf: 'flex-start', padding: '6px 14px', borderRadius: 8, background: '#FF6407', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999, padding: 16 },
    modalBox: { background: '#fff', borderRadius: 20, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 16, boxShadow: '0 8px 40px rgba(0,0,0,0.18)' },
    modalTitle: { margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' },
    questionBox: { background: '#FFF7ED', border: '1.5px solid #FDBA74', borderRadius: 10, padding: '12px 14px', fontSize: 14, color: '#92400E', lineHeight: 1.55 },
    label: { margin: 0, fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em' },
    textarea: { width: '100%', minHeight: 100, background: '#F9FAFB', border: '1.5px solid #E5E7EB', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', resize: 'vertical', outline: 'none', color: '#111827', boxSizing: 'border-box' },
    uploadBox: { border: '2px dashed #E5E7EB', borderRadius: 10, padding: '20px 14px', textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s', background: docFile ? '#F0FDF4' : '#FAFAFA', borderColor: docFile ? '#6EE7B7' : '#E5E7EB' },
    actionRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 },
    btnPrimary: { flex: 1, padding: '10px 14px', borderRadius: 10, background: '#FF6407', color: '#fff', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13, opacity: saving ? 0.6 : 1 },
    btnSuccess: { flex: 1, padding: '10px 14px', borderRadius: 10, background: '#10B981', color: '#fff', border: 'none', cursor: marking ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13, opacity: marking ? 0.6 : 1 },
    btnCancel: { padding: '10px 14px', borderRadius: 10, background: '#F3F4F6', color: '#374151', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13 },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: '#9CA3AF', pointerEvents: 'none' }}>🔍</span>
          <input className="adm-input" placeholder="Fragen durchsuchen…"
            style={{ paddingLeft: 36 }}
            value={search} onChange={e => handleSearch(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {['unanswered','answered','all'].map(sf => (
            <button key={sf}
              className={'adm-subtab' + (statusFilter === sf ? ' adm-subtab--active' : '')}
              onClick={() => { setStatusFilter(sf); load(search, sf); }}
            >
              {sf === 'unanswered' ? '⏳ Offen' : sf === 'answered' ? '✅ Beantwortet' : '🌍 Alle'}
            </button>
          ))}
        </div>
      </div>

      {!loading && (
        <p style={{ margin: 0, fontSize: 12, color: '#9CA3AF' }}>
          {questions.length} Frage{questions.length !== 1 ? 'n' : ''} gefunden
        </p>
      )}

      {loading ? (
        <p style={{ textAlign: 'center', color: '#9CA3AF', padding: '32px 0', margin: 0 }}>Lade…</p>
      ) : questions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: '#9CA3AF' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>💬</div>
          <p style={{ margin: 0, fontSize: 14 }}>
            {search ? 'Keine passenden Fragen gefunden.' : 'Keine offenen Fragen — FragHejVi hat alles beantwortet! 🎉'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {questions.map(q => (
            <div key={q.id} style={s.card}>
              <div style={s.cardHeader}>
                <div style={{ fontSize: 20, flexShrink: 0 }}>💬</div>
                <p style={s.questionText}>{q.question}</p>
                <span style={s.badge(q.status === 'answered')}>
                  {q.status === 'answered' ? '✅ Beantwortet' : '⏳ Offen'}
                </span>
              </div>
              <div style={s.meta}>
                <span>👤 {q.user_name || 'Unbekannt'}</span>
                <span>·</span>
                <span>🕐 {fmtDate(q.created_at)}</span>
                {q.element_id && <><span>·</span><span>📄 Element #{q.element_id}</span></>}
              </div>
              {q.answer_text && (
                <div style={{ fontSize: 12, color: '#6B7280', background: '#F0FDF4', borderRadius: 8, padding: '8px 10px', borderLeft: '3px solid #10B981' }}>
                  ✅ {q.answer_text.slice(0, 120)}{q.answer_text.length > 120 ? '…' : ''}
                </div>
              )}
              <button style={s.btnAnswer} onClick={() => openModal(q)}>
                {q.status === 'answered' ? '✏️ Bearbeiten' : '✍️ Beantworten'}
              </button>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div style={s.overlay} onClick={e => e.target === e.currentTarget && closeModal()}>
          <div style={s.modalBox}>
            <h2 style={s.modalTitle}>✍️ Frage beantworten</h2>

            <div>
              <p style={s.label}>Frage des Mitarbeiters</p>
              <div style={s.questionBox}>{modal.question}</div>
              <p style={{ margin: '4px 0 0', fontSize: 11, color: '#9CA3AF' }}>
                👤 {modal.user_name || '–'} · 🕐 {fmtDate(modal.created_at)}
              </p>
            </div>

            {modal.chat_context && (() => {
              let turns = [];
              try { turns = JSON.parse(modal.chat_context); } catch (_) {}
              if (!Array.isArray(turns) || turns.length === 0) return null;
              return (
                <details style={{ margin: 0 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', userSelect: 'none', listStyle: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span>💬</span>
                    <span>Gesprächskontext ({turns.length} vorherige Nachricht{turns.length !== 1 ? 'en' : ''})</span>
                    <span style={{ marginLeft: 'auto', fontSize: 10 }}>▼</span>
                  </summary>
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto', padding: '8px 0', borderTop: '1px solid #F3F4F6' }}>
                    {turns.map((turn, i) => {
                      const isBot = turn.role === 'assistant';
                      return (
                        <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: isBot ? 'flex-start' : 'flex-end', gap: 2 }}>
                          <span style={{ fontSize: 10, color: '#9CA3AF' }}>{isBot ? '🤖 HejVi' : '👤 Mitarbeiter'}</span>
                          <div style={{ maxWidth: '88%', padding: '7px 11px', borderRadius: 10, fontSize: 12, lineHeight: 1.5, background: isBot ? '#F3F4F6' : '#FFF7ED', color: isBot ? '#374151' : '#92400E', borderBottomRightRadius: isBot ? 10 : 2, borderBottomLeftRadius: isBot ? 2 : 10 }}>
                            {turn.content}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </details>
              );
            })()}

            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <p style={{ ...s.label, margin: 0 }}>
                  Antwort <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional wenn Dokument hochgeladen)</span>
                </p>
                <button
                  onClick={handleSuggest}
                  disabled={suggesting || saving}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 8, border: '1.5px solid #E5E7EB', background: suggesting ? '#F3F4F6' : '#FFFBF7', color: suggesting ? '#9CA3AF' : '#FF6407', fontWeight: 600, fontSize: 12, cursor: suggesting || saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.5 : 1, transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                >
                  {suggesting ? (
                    <><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', border: '2px solid #FF6407', borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite' }} />Suche & formuliere…</>
                  ) : (
                    <>✨ Antwort vorschlagen</>
                  )}
                </button>
              </div>
              <textarea
                style={{ ...s.textarea, borderColor: (suggesting || extracting) ? '#FDBA74' : '#E5E7EB', transition: 'border-color 0.2s' }}
                placeholder={extracting ? '⏳ Extrahiere Antwort aus Dokument…' : suggesting ? 'KI recherchiert…' : docFile ? 'Extrahierte Antwort — bitte prüfen und ggf. anpassen…' : 'Schreibe eine Antwort für den Mitarbeiter…'}
                value={answerText}
                onChange={e => setAnswerText(e.target.value)}
                readOnly={suggesting || extracting}
              />
              {answerText && !suggesting && !extracting && (
                <p style={{ margin: '3px 0 0', fontSize: 11, color: '#9CA3AF', textAlign: 'right' }}>
                  {answerText.length} Zeichen · wird automatisch in alle Sprachen übersetzt
                  {docFile && ' · inkl. Dokument-Link'}
                </p>
              )}
              {extracting && (
                <p style={{ margin: '3px 0 0', fontSize: 11, color: '#F59E0B', textAlign: 'right' }}>⏳ Dokument wird analysiert…</p>
              )}
            </div>

            <div>
              <p style={s.label}>
                Dokument hochladen
                <span style={{ color: '#9CA3AF', fontWeight: 400 }}>{' '}(optional — Antwort wird automatisch extrahiert & in CogRAG gespeichert)</span>
              </p>
              <div style={s.uploadBox}
                onClick={() => !extracting && fileRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f && !extracting) handleFileSelect(f); }}
              >
                {extracting ? (
                  <div style={{ color: '#F59E0B', fontSize: 13 }}>
                    <div style={{ fontSize: 24, marginBottom: 4 }}>⏳</div>
                    <div style={{ fontWeight: 600 }}>Antwort wird aus Dokument extrahiert…</div>
                    <div style={{ fontSize: 11, marginTop: 2, color: '#9CA3AF' }}>Einen Moment bitte</div>
                  </div>
                ) : docFile ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                    <span>📄</span>
                    <span style={{ fontSize: 13, color: '#065F46', fontWeight: 500 }}>{docFile.name}</span>
                    <button onClick={e => { e.stopPropagation(); setDocFile(null); setAnswerText(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 16 }}>✕</button>
                  </div>
                ) : (
                  <div style={{ color: '#9CA3AF', fontSize: 13 }}>
                    <div style={{ fontSize: 24, marginBottom: 4 }}>📎</div>
                    <div>PDF, DOCX, TXT, PPTX</div>
                    <div style={{ fontSize: 11, marginTop: 2 }}>Klicken oder Datei hierher ziehen — Antwort wird automatisch extrahiert</div>
                  </div>
                )}
                <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt,.pptx,.ppt" style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files[0]; if (f) handleFileSelect(f); e.target.value = ''; }} />
              </div>
              {docFile && !extracting && (
                <p style={{ margin: '4px 0 0', fontSize: 11, color: '#6B7280' }}>
                  📎 Dokument wird beim Speichern in HejVi/CogRAG importiert · Link wird automatisch in die Antwort eingefügt
                </p>
              )}
              {suggestedTitle && !extracting && (
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, background: '#F0F9FF', border: '1.5px solid #BAE6FD', borderRadius: 8, padding: '8px 12px' }}>
                  <span style={{ fontSize: 14, flexShrink: 0 }}>📌</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: '#0369A1', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Element-Titel (automatisch generiert)</p>
                    <p style={{ margin: '2px 0 0', fontSize: 13, fontWeight: 600, color: '#0C4A6E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{suggestedTitle}</p>
                  </div>
                </div>
              )}
            </div>

            {!answerText.trim() && !extracting && (
              <p style={{ margin: 0, fontSize: 12, color: '#EF4444' }}>
                {docFile ? '⚠ Extraktion wird noch ausgeführt — bitte kurz warten.' : '⚠ Bitte eine Textantwort eingeben oder ein Dokument hochladen (Antwort wird dann automatisch extrahiert).'}
              </p>
            )}
            {extracting && (
              <p style={{ margin: 0, fontSize: 12, color: '#F59E0B' }}>⏳ Antwort wird aus dem Dokument extrahiert — bitte warten…</p>
            )}

            <div style={s.actionRow}>
              <button style={s.btnCancel} onClick={closeModal}>Abbrechen</button>
              <button style={{ ...s.btnPrimary, opacity: canSave ? 1 : 0.4, cursor: canSave ? 'pointer' : 'not-allowed' }} onClick={handleSave} disabled={!canSave}>
                {saving ? '⏳ Speichert & übersetzt…' : extracting ? '⏳ Extraktion läuft…' : '💾 Speichern & Nutzer benachrichtigen'}
              </button>
              <button style={{ ...s.btnSuccess, opacity: canMark ? 1 : 0.4, cursor: canMark ? 'pointer' : 'not-allowed' }} onClick={handleMarkAnswered} disabled={!canMark} title={modal?.status === 'answered' ? 'Bereits beantwortet' : 'Status setzen ohne Benachrichtigung'}>
                {marking ? '⏳…' : modal?.status === 'answered' ? '✅ Bereits beantwortet' : '🔒 Schließen ohne Nachricht'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ─── COMPANY IDENTITY TAB ────────────────────────────────────────────────────

const CompanyIdentityTab = ({ toast }) => {
  const [docs, setDocs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [uploading, setUploading] = useState(false);
  const [replaceAll, setReplaceAll] = useState(false);
  const [dragOver, setDragOver]   = useState(false);
  const fileInputRef              = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api('/admin/company-documents');
      setDocs(res.data || []);
    } catch (e) {
      if (e.code !== 'FEATURE_GATED' && e.code !== 'LICENCE_EXPIRED') {
        if (!silent) toast('Fehler beim Laden der Unternehmens-Dokumente', 'error');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const fmt = (bytes) => {
    if (!bytes) return '–';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const statusLabel = (s) => {
    const map = {
      enriched: { label: 'Aktiv', color: '#15803D', bg: '#F0FDF4', border: '#BBF7D0' },
      done:     { label: 'Phase 1 ✓', color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE' },
      processing:{ label: 'Läuft…', color: '#92400E', bg: '#FFFBEB', border: '#FDE68A' },
      pending:  { label: 'Ausstehend', color: '#6B7280', bg: '#F9FAFB', border: '#E5E7EB' },
      failed:   { label: 'Fehler', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
    };
    const c = map[s] || map.pending;
    return (
      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, letterSpacing: '0.03em', color: c.color, background: c.bg, border: `1px solid ${c.border}` }}>{c.label}</span>
    );
  };

  const handleUpload = async (file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'text/plain', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const mime = file.type || '';
    if (!allowed.some(a => mime.includes(a.split('/')[1])) &&
        !file.name.match(/\.(pdf|txt|doc|docx)$/i)) {
      toast('Ungültiger Dateityp. Erlaubt: PDF, TXT, DOC, DOCX.', 'error');
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('document_file', file);
      if (replaceAll) fd.append('replace_all', '1');

      const token = localStorage.getItem('hejvi_access_token') || localStorage.getItem('token') || '';
      const { appConfig } = await import('../config');
      const res = await fetch(`${appConfig.apiBaseUrl}/admin/company-documents/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.status === 'ok') {
        toast(`„${file.name}" hochgeladen. Vektorisierung läuft.`, 'success');
        setTimeout(() => load(true), 1500);
      } else {
        toast(data.message || 'Upload fehlgeschlagen.', 'error');
      }
    } catch (e) {
      toast('Upload-Fehler: ' + e.message, 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`„${doc.original_name}" wirklich löschen?\nDies entfernt alle zugehörigen Chunks aus dem RAG.`)) return;
    try {
      await api('/admin/company-documents/delete', {
        method: 'POST',
        body: JSON.stringify({ file_storage_id: doc.id }),
      });
      toast(`„${doc.original_name}" gelöscht.`, 'success');
      load(true);
    } catch (e) {
      toast('Löschen fehlgeschlagen.', 'error');
    }
  };

  return (
    <div className="adm-tab-content">
      <div style={{ padding: '14px 18px', borderRadius: 12, marginBottom: 20, background: '#F0F9FF', border: '1px solid #BAE6FD' }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#0369A1', marginBottom: 6 }}>🏢 Unternehmensidentität</div>
        <p style={{ fontSize: 13, color: '#374151', margin: 0, lineHeight: 1.6 }}>
          Lade hier interne Dokumente hoch (Leitbild, Organigramm, FAQs, Urlaubsregelungen, Prozesse, Ansprechpartner).
          FragHejVi lernt daraus das Unternehmen kennen und antwortet mit <strong>„Wir"</strong>-Perspektive auf betriebliche Fragen.
          Diese Dokumente sind für alle Nutzer verfügbar — unabhängig von Lerngruppen oder Inhalten.
        </p>
      </div>

      <div
        className={`ci-dropzone${dragOver ? ' ci-dropzone--over' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleUpload(f); }}
        onClick={() => !uploading && fileInputRef.current?.click()}
      >
        <input ref={fileInputRef} type="file" accept=".pdf,.txt,.doc,.docx" style={{ display: 'none' }}
          onChange={e => { const f = e.target.files[0]; if (f) handleUpload(f); }} />
        {uploading ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 28, marginBottom: 8, animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</div>
            <div style={{ fontSize: 13, color: '#6B7280' }}>Wird hochgeladen und vektorisiert…</div>
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📎</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Dokument hier ablegen oder klicken</div>
            <div style={{ fontSize: 12, color: '#9CA3AF' }}>PDF, TXT, DOC, DOCX</div>
          </div>
        )}
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: 13, color: '#374151', cursor: 'pointer', userSelect: 'none' }}>
        <input type="checkbox" checked={replaceAll} onChange={e => setReplaceAll(e.target.checked)} style={{ width: 16, height: 16 }} />
        <span>Bestehende Unternehmens-Dokumente <strong>ersetzen</strong>{' '}<span style={{ color: '#6B7280' }}>(alle vorherigen Uploads löschen)</span></span>
      </label>

      <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 10 }}>
        Aktuelle Unternehmens-Dokumente ({docs.length})
        <button className="adm-btn adm-btn--ghost adm-btn--xs" style={{ marginLeft: 10 }} onClick={() => load(false)}>↻ Aktualisieren</button>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>Lade…</div>}
      {!loading && docs.length === 0 && (
        <div style={{ textAlign: 'center', padding: 32, borderRadius: 12, background: '#F9FAFB', border: '1px dashed #E5E7EB', color: '#9CA3AF' }}>
          Noch keine Unternehmens-Dokumente hochgeladen.
        </div>
      )}
      {!loading && docs.map(doc => (
        <div key={doc.id} className="adm-card" style={{ marginBottom: 10 }}>
          <div className="adm-card__header">
            <div>
              <div className="adm-card__title" style={{ fontSize: 14 }}>📄 {doc.original_name}</div>
              <div className="adm-card__sub" style={{ marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <span>{fmt(doc.file_size)}</span>
                <span>·</span>
                <span>{new Date(doc.created_at).toLocaleDateString('de-DE')}</span>
                <span>·</span>
                {statusLabel(doc.embedding_status)}
                {doc.chunks_total > 0 && (
                  <><span>·</span><span style={{ fontSize: 11, color: '#6B7280' }}>{doc.chunks_enriched}/{doc.chunks_total} Chunks angereichert</span></>
                )}
              </div>
            </div>
            <div className="adm-card__actions">
              <button className="adm-btn adm-btn--danger-ghost adm-btn--xs" onClick={() => handleDelete(doc)}>🗑 Löschen</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// =============================================================================
// LICENCE TAB
// Zwei Ansichten:
//   Super-Admin (group 0) → alle Mandanten in Tabelle, bearbeitbar
//   Tenant-Admin          → eigene Lizenz als Read-only-Dashboard
// =============================================================================

/** Fortschrittsbalken für Seat-Auslastung */
const SeatsProgressBar = ({ label, used, total, barColor }) => {
  const unlimited = total === 0;
  const pct       = unlimited ? 0 : Math.min(100, Math.round(used / total * 100));
  const avail     = unlimited ? null : Math.max(0, total - used);
  const danger    = !unlimited && pct >= 90;
  const warn      = !unlimited && pct >= 70 && pct < 90;
  const color     = danger ? '#ef4444' : warn ? '#f59e0b' : barColor;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{label}</span>
        <span style={{ fontSize: 12, color: danger ? '#ef4444' : '#64748b' }}>
          {unlimited
            ? 'Unbegrenzt'
            : `${used} / ${total} belegt · ${avail} frei`}
        </span>
      </div>
      {!unlimited && (
        <div style={{ height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 0.4s ease' }} />
        </div>
      )}
    </div>
  );
};

/** Tenant-Admin: eigene Lizenz lesen (+ bearbeiten wenn onEdit übergeben) */
const TenantLicenceView = ({ licence, onEdit }) => {
  const langName = (code) => {
    const e = Object.values(LANG_MAP).find(l => l.code === code);
    return e ? e.label : (code || '?').toUpperCase();
  };

  if (!licence) {
    return <p style={{ color: '#94a3b8', padding: 24 }}>Keine Lizenzdaten verfügbar.</p>;
  }

  const validUntil = licence.valid_until
    ? new Date(licence.valid_until).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })
    : 'Unbegrenzt';

  return (
    <div style={{ maxWidth: 560 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 20px', color: '#1e293b' }}>
        Ihre Lizenz · {licence.tenant_name}
      </h3>

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px', marginBottom: 16 }}>
        <SeatsProgressBar label="⭐ Full-Seats (inkl. Content-Erstellung)" used={licence.seats_full_used} total={licence.seats_full_total} barColor="#3b82f6" />
        <SeatsProgressBar label="Essentials-Seats" used={licence.seats_essentials_used} total={licence.seats_essentials_total} barColor="#8b5cf6" />
      </div>

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 24px', marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 4 }}>Gültig bis</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{validUntil}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 4 }}>Stammsprache</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{langName(licence.base_language || 'de')}</div>
          </div>
        </div>
        {licence.ui_languages?.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 6 }}>Aktive UI-Sprachen</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {licence.ui_languages.map(code => (
                <span key={code} className="adm-chip" style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>{langName(code)}</span>
              ))}
            </div>
          </div>
        )}
        {licence.tts_languages?.length > 0 && (
          <div>
            <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 6 }}>TTS-Sprachen</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {licence.tts_languages.map(code => (
                <span key={code} className="adm-chip" style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }}>{langName(code)}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#92400e', lineHeight: 1.6 }}>
        <strong>ℹ️ Seat-Typen:</strong><br />
        <strong>Full-Seats</strong> — alle Features inkl. VideoBuilder &amp; TTS.<br />
        <strong>Essentials</strong> — alle Features außer Content-Erstellung.
      </div>

      {onEdit && (
        <div style={{ marginTop: 20 }}>
          <button
            className="adm-btn adm-btn--primary"
            onClick={() => onEdit(licence)}
          >
            ✏️ Lizenz bearbeiten
          </button>
        </div>
      )}
    </div>
  );
};

/** Super-Admin: Bearbeitungs-Modal für eine Lizenz */
const LicenceEditModal = ({ licence, onClose, onSaved, toast }) => {
  const [form, setForm] = useState({
    tenant_id:              licence.tenant_id,
    seats_full_total:       licence.seats_full_total,
    seats_essentials_total: licence.seats_essentials_total,
    valid_until:            licence.valid_until || '',
    base_language:          licence.base_language || 'de',
    ui_languages:           licence.ui_languages  || ['de'],
    tts_languages:          licence.tts_languages || ['de'],
  });
  const [saving, setSaving] = useState(false);

  const allLangs = Object.entries(LANG_MAP).map(([id, { code, label }]) => ({ code, label, id: Number(id) }));

  const toggleLang = (code, key) => {
    setForm(f => {
      const cur  = f[key] || [];
      const next = cur.includes(code) ? cur.filter(l => l !== code) : [...cur, code];
      // Stammsprache kann nicht aus UI-Sprachen entfernt werden
      if (key === 'ui_languages' && !next.includes(f.base_language)) return f;
      // TTS-Sprachen dürfen nur aktive UI-Sprachen sein
      if (key === 'tts_languages' && !f.ui_languages.includes(code)) return f;
      return { ...f, [key]: next };
    });
  };

  const setBase = (code) => {
    setForm(f => ({
      ...f,
      base_language: code,
      ui_languages: f.ui_languages.includes(code) ? f.ui_languages : [...f.ui_languages, code],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api('/admin/licence/update', { method: 'POST', body: JSON.stringify(form) });
      onSaved();
    } catch (e) {
      toast(e.message || 'Fehler beim Speichern', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Lizenz: ${licence.tenant_name}`} onClose={onClose} wide>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Field label="⭐ Full-Seats gesamt" hint="0 = unbegrenzt">
          <input type="number" min={0} className="adm-input"
            value={form.seats_full_total}
            onChange={e => setForm(f => ({ ...f, seats_full_total: Math.max(0, Number(e.target.value)) }))} />
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>Belegt: {licence.seats_full_used}</div>
        </Field>
        <Field label="Essentials-Seats gesamt" hint="0 = unbegrenzt">
          <input type="number" min={0} className="adm-input"
            value={form.seats_essentials_total}
            onChange={e => setForm(f => ({ ...f, seats_essentials_total: Math.max(0, Number(e.target.value)) }))} />
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>Belegt: {licence.seats_essentials_used}</div>
        </Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Field label="Gültig bis" hint="Leer = unbegrenzt">
          <input type="date" className="adm-input"
            value={form.valid_until || ''}
            onChange={e => setForm(f => ({ ...f, valid_until: e.target.value || '' }))} />
        </Field>
        <Field label="Stammsprache">
          <select className="adm-input" value={form.base_language} onChange={e => setBase(e.target.value)}>
            {allLangs.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </Field>
      </div>

      <Field label="UI-Sprachen (aktive App-Sprachen)">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '6px 0' }}>
          {allLangs.map(l => {
            const isBase    = l.code === form.base_language;
            const isChecked = form.ui_languages.includes(l.code);
            return (
              <label key={l.code} style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '4px 10px',
                background: isChecked ? '#eff6ff' : '#f8fafc',
                border: `1px solid ${isChecked ? '#bfdbfe' : '#e2e8f0'}`,
                borderRadius: 6, cursor: isBase ? 'not-allowed' : 'pointer', fontSize: 13,
              }}>
                <input type="checkbox" checked={isChecked} disabled={isBase}
                  onChange={() => toggleLang(l.code, 'ui_languages')} style={{ margin: 0 }} />
                {l.label}
                {isBase && <span style={{ fontSize: 10, color: '#94a3b8' }}>(Basis)</span>}
              </label>
            );
          })}
        </div>
      </Field>

      <Field label="TTS-Sprachen (Text-to-Speech)" hint="Nur aktive UI-Sprachen wählbar">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '6px 0' }}>
          {allLangs.filter(l => form.ui_languages.includes(l.code)).map(l => {
            const isChecked = form.tts_languages.includes(l.code);
            return (
              <label key={l.code} style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '4px 10px',
                background: isChecked ? '#f0fdf4' : '#f8fafc',
                border: `1px solid ${isChecked ? '#bbf7d0' : '#e2e8f0'}`,
                borderRadius: 6, cursor: 'pointer', fontSize: 13,
              }}>
                <input type="checkbox" checked={isChecked}
                  onChange={() => toggleLang(l.code, 'tts_languages')} style={{ margin: 0 }} />
                {l.label}
              </label>
            );
          })}
        </div>
      </Field>

      <div className="adm-modal__footer">
        <button className="adm-btn adm-btn--ghost" onClick={onClose}>Abbrechen</button>
        <button className="adm-btn adm-btn--primary" onClick={save} disabled={saving}>
          {saving ? 'Speichert…' : 'Lizenz speichern'}
        </button>
      </div>
    </Modal>
  );
};

/** Super-Admin: Alle Mandanten in Tabelle */
const SuperAdminLicenceView = ({ licences, onEdit }) => {
  const [search, setSearch] = useState('');

  const langName = (code) => {
    const e = Object.values(LANG_MAP).find(l => l.code === code);
    return e ? e.label : (code || '').toUpperCase();
  };

  const filtered = licences.filter(function(l) {
    if (!search) return true;
    return (l.tenant_name || '').toLowerCase().indexOf(search.toLowerCase()) !== -1;
  });

  function SeatInfo(props) {
    var used  = props.used  || 0;
    var total = props.total || 0;
    var label = props.label;
    var color = props.color;
    var p = total > 0 ? Math.min(100, Math.round(used / total * 100)) : 0;
    var danger = total > 0 && p >= 90;
    return (
      React.createElement('div', { style: { minWidth: 110 } },
        React.createElement('div', { style: { fontSize: 11, color: '#94a3b8', marginBottom: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' } }, label),
        total === 0
          ? React.createElement('span', { style: { fontSize: 18, fontWeight: 700, color: '#22c55e' } }, '∞')
          : React.createElement('div', null,
              React.createElement('div', { style: { fontSize: 13, fontWeight: 700, color: danger ? '#ef4444' : '#1e293b' } }, used + ' / ' + total),
              React.createElement('div', { style: { width: 72, height: 5, background: '#e2e8f0', borderRadius: 3, marginTop: 4 } },
                React.createElement('div', { style: { width: p + '%', height: '100%', background: danger ? '#ef4444' : color, borderRadius: 3 } })
              )
            )
      )
    );
  }

  return (
    React.createElement('div', null,
      React.createElement('div', { className: 'adm-tab-toolbar' },
        React.createElement(SearchBar, { value: search, onChange: setSearch, placeholder: 'Mandant suchen…' }),
        React.createElement('span', { style: { fontSize: 13, color: '#94a3b8', padding: '0 8px' } },
          filtered.length + ' Mandant' + (filtered.length !== 1 ? 'en' : '')
        )
      ),
      React.createElement('div', { style: { marginTop: 8 } },
        filtered.length === 0 && React.createElement('p', { style: { color: '#94a3b8', padding: 16 } }, 'Keine Mandanten gefunden'),
        filtered.map(function(l, i) {
          return React.createElement('div', {
            key: (l.tenant_id || i).toString(),
            style: { border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 18px', marginBottom: 10, background: '#fff', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }
          },
            React.createElement('div', { style: { flex: '1 1 160px' } },
              React.createElement('div', { style: { fontWeight: 700, fontSize: 14, color: '#1e293b' } }, l.tenant_name || '–'),
              React.createElement('div', { style: { fontSize: 11, color: '#94a3b8', marginTop: 2 } }, 'Tier ' + (l.tier || '?') + ' · ' + (l.valid_until ? new Date(l.valid_until).toLocaleDateString('de-DE') : '∞'))
            ),
            React.createElement(SeatInfo, { label: 'Full', used: l.seats_full_used, total: l.seats_full_total, color: '#3b82f6' }),
            React.createElement(SeatInfo, { label: 'Essentials', used: l.seats_essentials_used, total: l.seats_essentials_total, color: '#8b5cf6' }),
            React.createElement('div', { style: { flex: '1 1 120px' } },
              React.createElement('div', { style: { fontSize: 11, color: '#94a3b8', marginBottom: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' } }, 'Sprachen'),
              React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4 } },
                (l.ui_languages || []).map(function(code) {
                  return React.createElement('span', {
                    key: code,
                    style: { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 4, padding: '1px 6px', fontSize: 11, fontWeight: 600 }
                  }, code.toUpperCase());
                })
              )
            ),
            React.createElement('button', {
              onClick: function() { onEdit(l); },
              style: { background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer', color: '#475569', whiteSpace: 'nowrap', flexShrink: 0 }
            }, '✏️ Bearbeiten')
          );
        })
      )
    )
  );
};
/** Haupt-Tab-Komponente */
const LicenceTab = ({ toast }) => {
  const [licences, setLicences]       = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading]         = useState(true);
  const [editLicence, setEditLicence] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('/admin/licence');
      setLicences(res.data || []);
      setIsSuperAdmin(res.is_super_admin || false);
    } catch (e) {
      toast(e.message || 'Fehler beim Laden der Lizenzdaten', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="adm-loading"><span className="adm-spinner" /></div>;

  return (
    <div className="adm-tab-content">
      {isSuperAdmin ? (
        <SuperAdminLicenceView
          licences={licences}
          onEdit={setEditLicence}
        />
      ) : (
        <TenantLicenceView licence={licences[0]} onEdit={setEditLicence} />
      )}

      {editLicence && (
        <LicenceEditModal
          licence={editLicence}
          onClose={() => setEditLicence(null)}
          onSaved={() => { setEditLicence(null); load(); toast('Lizenz gespeichert', 'success'); }}
          toast={toast}
        />
      )}
    </div>
  );
};

// =============================================================================
// END LICENCE TAB
// =============================================================================

const AdminPanel = () => {
  const navigate                    = useNavigate();
  const { t, currentLanguage }      = useLanguage();
  const [activeTab, setActiveTab]   = useState(
    () => sessionStorage.getItem('hejvi_admin_tab') || 'users'
  );
  const [stats, setStats]           = useState(null);
  const [allGroups, setAllGroups]   = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [allUsers, setAllUsers]     = useState([]);
  const [toasts, setToasts]         = useState([]);
  const toastIdRef                  = useRef(0);
  // ── Guard via features.admin_panel ─────────────────────────────────────
  const { features, hasFeature } = useAuth();
  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem('hejvi_user') || '{}'); } catch { return {}; }
  })();
  const userGroup = storedUser?.group ?? storedUser?.group_id ?? 99;
  const isAllowed = features.admin_panel; // ersetzt ADMIN_GROUPS.includes(Number(userGroup))

  // ── Toast helper ────────────────────────────────────────────────────────
  const toast = useCallback((message, type = 'success') => {
    const id = ++toastIdRef.current;
    setToasts(prev => [...prev, { id, message, type }]);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(ti => ti.id !== id));
  }, []);

  // ── Session-expiry detection ─────────────────────────────────────────────
  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem('hejvi_access_token');
    localStorage.removeItem('hejvi_refresh_token');
    localStorage.removeItem('hejvi_token_expiry');
    localStorage.removeItem('hejvi_user');
    navigate('/login');
  }, [navigate]);

  useEffect(() => {
    if (!isAllowed) return;
    const check = async () => {
      const expiry = parseInt(localStorage.getItem('hejvi_token_expiry') || '0', 10);
      if (!expiry) return;
      if (Date.now() < expiry) return;

      const refreshTok = localStorage.getItem('hejvi_refresh_token');
      if (!refreshTok) { handleUnauthorized(); return; }
      try {
        const { appConfig } = await import('../config');
        const res = await fetch(`${appConfig.apiBaseUrl}/refresh`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${refreshTok}`, 'Content-Type': 'application/json' },
        });
        if (res.ok) {
          const data = await res.json();
          localStorage.setItem('hejvi_access_token', data.access_token);
          if (data.refresh_token) {
            localStorage.setItem('hejvi_refresh_token', data.refresh_token);
          }
          localStorage.setItem('hejvi_token_expiry', String(Date.now() + 15 * 60 * 1000));
          // Features an AuthContext weitergeben ohne Re-Login
          if (data.features) {
            window.dispatchEvent(new CustomEvent('hejvi:features-updated', { detail: data.features }));
          }
        } else {
          handleUnauthorized();
        }
      } catch {
        // Network error — don't force logout
      }
    };
    check();
    const interval = setInterval(check, 60_000);
    return () => clearInterval(interval);
  }, [isAllowed, handleUnauthorized]);

  // ── Load shared data ────────────────────────────────────────────────────
  const loadGroups = useCallback(async () => {
    try {
      const res = await api('/admin/usergroups');
      setAllGroups(res.data || []);
    } catch (e) {
      if (e.message?.includes('401')) handleUnauthorized();
    }
  }, [handleUnauthorized]);

  const loadStats = useCallback(async () => {
    try {
      const res = await api('/admin/stats');
      setStats(res.data);
    } catch (e) {
      if (e.message?.includes('401')) handleUnauthorized();
    }
  }, [handleUnauthorized]);

  const loadClients = useCallback(async () => {
    try {
      const res = await api('/admin/clients');
      setAllClients(res.data || []);
    } catch {
      // Silently ignore — backend now returns only own client(s)
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await api('/admin/users');
      setAllUsers(res.data || []);
    } catch (e) {
      if (e.message?.includes('401')) handleUnauthorized();
    }
  }, [handleUnauthorized]);

  useEffect(() => {
    if (!isAllowed) return;
    loadGroups();
    loadStats();
    loadClients();
    loadUsers();
  }, [isAllowed, loadGroups, loadStats, loadClients, loadUsers]);

    // tabs-Definition VOR dem Guard — wird für den useEffect unten benötigt
  // Sichtbarkeitsregeln:
  //   Basis-Tabs:     immer sichtbar für alle mit admin_panel
  //   plan+role-Tabs: backend setzt admin_frag_hejvi / admin_document_chat etc.
  //                   (group 0/3 via admin_role_config, group 2 via plan-feature direkt)
  //   super_admin:    Group 0 hat immer alle Tabs (Fallback-Guard)
  const isSA = hasFeature('super_admin');

  // ── Tab-Sichtbarkeitsregeln ──────────────────────────────────────────────
  // Jeder Tab wird exakt durch das zugehörige Backend-Feature gesteuert.
  // Das Backend (buildFeaturesObject) setzt admin_* Flags korrekt pro Gruppe:
  //
  //   Group 0 (SuperAdmin):   alle Tabs immer (isSA-Fallback + Schluss-Override)
  //   Group 3 (TenantAdmin):  user_management, group_management, content_*, analytics
  //                           + plan-gated Tabs wenn via admin_role_config konfiguriert
  //   Group 2 (Supervisor):   NUR content_management, content_approval
  //                           + plan-gated Tabs wenn via admin_role_config konfiguriert
  //                           NIEMALS user_management oder group_management
  //
  // Basis-Tabs sind durch ihr eigenes feature gegated (nicht mehr ungegated für alle).
  const tabs = [
    // User-Management: nur Group 0+3 (features.user_management)
    ...(hasFeature('user_management') ? [
      { id: 'users',      label: '👥 ' + t('adm_users') },
    ] : []),
    // Group-Management: nur Group 0+3 (features.group_management)
    ...(hasFeature('group_management') ? [
      { id: 'usergroups', label: '🏷️ ' + t('adm_groups') },
    ] : []),
    // Content-Management: Group 0+2+3 (features.content_management)
    ...(hasFeature('content_management') ? [
      { id: 'collections', label: '📚 ' + t('adm_topics') },
      { id: 'elements',    label: '🎬 ' + t('adm_videos') },
    ] : []),
    // Nachrichten + Archiv: konfigurierbar via admin_role_config ('notifications'-Tab)
    //   Group 0:   isSA-Fallback (immer sichtbar)
    //   Group 3/2: admin_notifications (via admin_role_config, default: an)
    ...(isSA || hasFeature('admin_notifications') ? [
      { id: 'notifications', label: '📢 ' + (t('adm_notifications') || 'Nachrichten') },
      { id: 'archive',       label: '📋 Archiv' },
    ] : []),
    // Dokumente & RAG / FragHejVi:
    //   Group 0:   isSA-Fallback
    //   Group 3:   admin_document_chat / admin_frag_hejvi (via admin_role_config + Plan)
    //   Group 2:   admin_document_chat / admin_frag_hejvi (via admin_role_config)
    //              KEIN direkter plan-feature Fallback — würde Group 2 unkontrolliert öffnen
    ...(isSA || hasFeature('admin_document_chat') || hasFeature('admin_frag_hejvi') ? [
      { id: 'documents', label: '📄 ' + (t('adm_docs') || 'Dokumente & RAG') },
      { id: 'fraghejvi', label: '💬 FragHejVi' },
    ] : []),
    // Unternehmensidentität:
    //   Group 0:   isSA-Fallback
    //   Group 3/2: admin_company_knowledge (via admin_role_config + Plan)
    ...(isSA || hasFeature('admin_company_knowledge') ? [
      { id: 'company', label: '🏢 Unternehmensidentität' },
    ] : []),
    // Domain Knowledge:
    //   Group 0:   isSA-Fallback
    //   Group 3/2: admin_domain_knowledge (via admin_role_config + Plan)
    ...(isSA || hasFeature('admin_domain_knowledge') ? [
      { id: 'domain', label: '🔩 Domain Knowledge' },
    ] : []),
    // Lizenzen: nur Group 0+3 (analytics-Feature als Proxy, da eigenes Feature fehlt)
    ...(hasFeature('analytics') ? [
      { id: 'licences', label: '🔑 Lizenzen' },
    ] : []),
    // Wissensbasis:
    //   Group 0:   isSA-Fallback
    //   Group 3:   admin_knowledge_base (via admin_role_config + Plan)
    //   Group 2:   admin_knowledge_base (via admin_role_config)
    ...(isSA || hasFeature('admin_knowledge_base') ? [
      { id: 'knowledge', label: '🧠 Wissensbasis' },
    ] : []),
  ];

  // Falls gespeicherter Tab durch Tier-Filter nicht mehr verfügbar → auf 'users' zurückfallen
  // Muss VOR dem Guard stehen (Hook-Reihenfolge)
  useEffect(() => {
    const validTabIds = tabs.map(tab => tab.id);
    if (!validTabIds.includes(activeTab)) {
      // 'users' könnte für Group 2 nicht verfügbar sein — auf ersten verfügbaren Tab fallen
      const fallback = validTabIds[0] || 'collections';
      setActiveTab(fallback);
      sessionStorage.setItem('hejvi_admin_tab', fallback);
    }
  }, [features]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Guard return — must come AFTER all hooks ─────────────────────────────
  if (!isAllowed) {
    return (
      <div className="adm-denied">
        <div className="adm-denied__inner">
          <span className="adm-denied__icon">🔒</span>
          <h2>{t('adm_access_denied')}</h2>
          <p>{t('adm_access_denied_desc')}</p>
          <button className="adm-btn adm-btn--primary" onClick={() => navigate('/')}>{t('adm_back')}</button>
        </div>
      </div>
    );
  }

  const switchTab = (id) => { setActiveTab(id); sessionStorage.setItem('hejvi_admin_tab', id); };

  return (
    <div className="adm-root">
      {/* Toast container */}
      <div className="adm-toast-container">
        {toasts.map(ti => (
          <Toast key={ti.id} message={ti.message} type={ti.type} onClose={() => dismissToast(ti.id)} />
        ))}
      </div>

      {/* Header */}
      <header className="adm-header">
        <div className="adm-header__left">
          <button className="adm-back-btn" onClick={() => navigate(-1)}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M11 4L6 9L11 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div>
            <h1 className="adm-header__title">Admin</h1>
            <p className="adm-header__sub">{t('adm_subtitle')}</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto', paddingLeft: 16, flexShrink: 0 }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0,
          }}>
            {(storedUser?.name || '?').charAt(0).toUpperCase()}
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {storedUser?.name || '–'}
          </span>
        </div>
        <StatsBar stats={stats} onTabClick={switchTab} validTabIds={tabs.map(t => t.id)} />
      </header>

      {/* Nav tabs */}
      <nav className="adm-nav">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`adm-nav__tab ${activeTab === tab.id ? 'adm-nav__tab--active' : ''}`}
            onClick={() => switchTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Tab content */}
      <main className="adm-main">
        {activeTab === 'users' && (
          <UsersTab toast={toast} allGroups={allGroups} allClients={allClients} allUsers={allUsers} />
        )}
        {activeTab === 'usergroups' && (
          <UsergroupsTab toast={toast} allGroups={allGroups} reloadGroups={loadGroups} allUsers={allUsers} />
        )}
        {activeTab === 'collections' && (
          <CollectionsTab toast={toast} allGroups={allGroups} />
        )}
        {activeTab === 'elements' && (
          <ElementsTab toast={toast} allGroups={allGroups} />
        )}
        {activeTab === 'notifications' && (
          <SendMessagePanel
            makeAuthenticatedRequest={api}
            users={allUsers}
            usergroups={allGroups}
          />
        )}
        {activeTab === 'archive' && (
          <NotificationArchiveTab toast={toast} />
        )}
        {activeTab === 'documents' && (
          <RagTab toast={toast} />
        )}
        {activeTab === 'fraghejvi' && (
          <FragHejViTab toast={toast} />
        )}
        {activeTab === 'company' && (
          <CompanyIdentityTab toast={toast} />
        )}
        {activeTab === 'domain' && (
          hasFeature('admin_domain_knowledge') ? (
            <KnowledgeBaseTab makeAuthenticatedRequest={api} mode="domain" />
          ) : null
        )}
        {activeTab === 'licences' && (
          <LicenceTab toast={toast} />
        )}
        {activeTab === 'knowledge' && (hasFeature('admin_knowledge_base') || hasFeature('knowledge_base')) && (
          <KnowledgeBaseTab makeAuthenticatedRequest={api} />
        )}
      </main>
    </div>
  );
};

export default AdminPanel;
